import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import Papa from 'papaparse';
import api from './api';
import EmailDesigner, { blocksToHtml, createBlock } from './components/EmailDesigner';
import CampaignFlow from './components/CampaignFlow';

// ── Theme ──────────────────────────────────────────────
const C = {
    bg: '#0a0a0f', card: '#12121a', hover: '#1a1a26', active: '#222233',
    border: '#2a2a3d', borderLight: '#3a3a55',
    text: '#e8e8f0', muted: '#8888a8', dim: '#5a5a78',
    accent: '#6366f1', accentHover: '#818cf8', accentDim: 'rgba(99,102,241,0.15)',
    success: '#22c55e', successDim: 'rgba(34,197,94,0.15)',
    warning: '#f59e0b', warningDim: 'rgba(245,158,11,0.15)',
    danger: '#ef4444', dangerDim: 'rgba(239,68,68,0.15)',
    info: '#06b6d4', infoDim: 'rgba(6,182,212,0.15)',
};

const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 8, boxSizing: 'border-box',
    border: `1px solid ${C.border}`, background: C.hover,
    color: C.text, fontSize: 14, outline: 'none', fontFamily: 'inherit',
};

const btnPrimary = {
    padding: '10px 24px', borderRadius: 8, border: 'none',
    background: C.accent, color: '#fff', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
};

const btnSecondary = {
    padding: '10px 24px', borderRadius: 8, border: `1px solid ${C.border}`,
    background: 'transparent', color: C.muted, fontSize: 13,
    cursor: 'pointer', fontFamily: 'inherit',
};

const btnDanger = {
    padding: '10px 24px', borderRadius: 8, border: `1px solid ${C.danger}`,
    background: 'transparent', color: C.danger, fontSize: 13,
    cursor: 'pointer', fontFamily: 'inherit',
};

const labelStyle = { display: 'block', fontSize: 12, color: C.muted, marginBottom: 6, fontWeight: 500 };

// ── Auth Context ───────────────────────────────────────
const AuthContext = createContext(null);

function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('relay_token');
        if (token) {
            api.me().then(u => {
                setUser(u);
                if (u.current_account && !localStorage.getItem('relay_account_id')) {
                    localStorage.setItem('relay_account_id', u.current_account.id);
                }
            }).catch(() => localStorage.removeItem('relay_token')).finally(() => setLoading(false));
        } else { setLoading(false); }
    }, []);

    const login = async (email, password) => {
        const data = await api.login(email, password);
        localStorage.setItem('relay_token', data.token);
        if (data.current_account) localStorage.setItem('relay_account_id', data.current_account.id);
        setUser(data.user);
    };

    const register = async (email, password, name, accountName) => {
        const data = await api.register(email, password, name, accountName);
        localStorage.setItem('relay_token', data.token);
        if (data.account_id) localStorage.setItem('relay_account_id', data.account_id);
        setUser(data.user);
    };

    const logout = () => { setUser(null); api.logout(); };

    if (loading) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: C.bg, color: C.muted }}>Loading...</div>
    );

    return <AuthContext.Provider value={{ user, login, register, logout }}>{children}</AuthContext.Provider>;
}

function useAuth() { return useContext(AuthContext); }

// ── Shared Components ──────────────────────────────────
function StatusBadge({ status }) {
    const map = {
        active: { color: C.success, bg: C.successDim }, paused: { color: C.warning, bg: C.warningDim },
        draft: { color: C.muted, bg: C.active }, completed: { color: C.info, bg: C.infoDim },
        scheduled: { color: '#a78bfa', bg: '#a78bfa22' },
        warming: { color: C.warning, bg: C.warningDim }, bounced: { color: C.danger, bg: C.dangerDim },
        unsubscribed: { color: C.dim, bg: C.active }, owner: { color: C.accent, bg: C.accentDim },
        admin: { color: C.info, bg: C.infoDim }, viewer: { color: C.muted, bg: C.active },
    };
    const s = map[status] || map.draft;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, color: s.color, background: s.bg }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color }} />
            {status}
        </span>
    );
}

function HealthRing({ value, size = 36 }) {
    const r = (size - 4) / 2;
    const circ = 2 * Math.PI * r;
    const offset = circ - (value / 100) * circ;
    const color = value > 90 ? C.success : value > 75 ? C.warning : C.danger;
    return (
        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={C.active} strokeWidth="3" />
                <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="3"
                    strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" />
            </svg>
            <span style={{ position: 'absolute', fontSize: 10, fontWeight: 700, color: C.text, fontFamily: 'monospace' }}>{value}</span>
        </div>
    );
}

function WorkspaceAvatar({ workspace, size = 42 }) {
    const fallback = {
        gmail: 'G', smtp: 'S', sendgrid: 'S', mailgun: 'M', resend: 'R', ses: 'A',
    }[workspace.provider] || (workspace.display_name || workspace.email || '?').charAt(0).toUpperCase();

    return (
        <div style={{
            position: 'relative', width: size, height: size, flex: `0 0 ${size}px`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', borderRadius: '50%', border: `1px solid ${C.borderLight}`,
            background: C.accentDim, color: C.accent, fontSize: Math.round(size * 0.38), fontWeight: 700,
        }} aria-label={`${workspace.display_name || workspace.email} profile image`}>
            {fallback}
            {workspace.profile_image_url && (
                <img
                    key={workspace.profile_image_url}
                    src={workspace.profile_image_url}
                    alt=""
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={event => { event.currentTarget.style.display = 'none'; }}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                />
            )}
        </div>
    );
}

function Toast({ message, type = 'success', onClose }) {
    useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
    const colors = { success: C.success, error: C.danger, info: C.info, warning: C.warning };
    return (
        <div className="toast" style={{ position: 'fixed', top: 20, right: 20, padding: '12px 20px', borderRadius: 8,
            background: C.card, border: `1px solid ${colors[type]}`, color: colors[type],
            fontSize: 13, fontWeight: 500, zIndex: 1000, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', maxWidth: 400 }}>
            {message}
        </div>
    );
}

function Modal({ title, onClose, children, width = 500 }) {
    return (
        <div className="modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
            onClick={onClose}>
            <div className="modal-content" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, width, maxWidth: '90vw', padding: 24, maxHeight: '85vh', overflowY: 'auto' }}
                onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <h3 style={{ margin: 0, fontSize: 18 }}>{title}</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 18 }}>✕</button>
                </div>
                {children}
            </div>
        </div>
    );
}

function HtmlImportButton({ onImport, onError, label = '↑ Import HTML' }) {
    const fileInputRef = useRef(null);

    const handleFile = async event => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        const isHtml = /\.html?$/i.test(file.name) || file.type === 'text/html';
        if (!isHtml) {
            onError?.('Choose an .html or .htm file.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            onError?.('HTML files must be smaller than 5 MB.');
            return;
        }

        try {
            const html = await file.text();
            if (!html.trim()) throw new Error('The selected HTML file is empty.');
            onImport(html, file.name);
        } catch (error) {
            onError?.(error.message || 'Could not read that HTML file.');
        }
    };

    return (
        <>
            <button type="button" onClick={() => fileInputRef.current?.click()}
                style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11, whiteSpace: 'nowrap' }}>
                {label}
            </button>
            <input ref={fileInputRef} type="file" accept=".html,.htm,text/html" onChange={handleFile} style={{ display: 'none' }} />
        </>
    );
}

const escapePreviewText = value => String(value || '')
    .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

function EmailPreviewModal({ subject, html, text, onClose }) {
    const [viewport, setViewport] = useState('desktop');
    const previewDocument = html?.trim() || `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head><body style="margin:0;padding:32px;font:16px/1.6 Arial,sans-serif;color:#202124;white-space:pre-wrap;">${escapePreviewText(text)}</body></html>`;

    return (
        <Modal title="Email preview" onClose={onClose} width={1000}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontSize: 10, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Subject</div>
                    <div style={{ color: C.text, fontSize: 14, marginTop: 3 }}>{subject || '(No subject)'}</div>
                </div>
                <div style={{ display: 'flex', border: `1px solid ${C.border}`, borderRadius: 7, overflow: 'hidden' }}>
                    {[
                        { key: 'desktop', label: '▣ Desktop' },
                        { key: 'mobile', label: '▯ Mobile' },
                    ].map(option => (
                        <button key={option.key} type="button" onClick={() => setViewport(option.key)}
                            style={{ padding: '7px 12px', border: 0, background: viewport === option.key ? C.accentDim : C.hover,
                                color: viewport === option.key ? C.accent : C.muted, cursor: 'pointer', fontSize: 11 }}>
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>
            <div style={{ background: '#08080c', border: `1px solid ${C.border}`, borderRadius: 10, padding: viewport === 'mobile' ? 16 : 0, overflow: 'auto' }}>
                <iframe title="Rendered email preview" srcDoc={previewDocument} sandbox="" referrerPolicy="no-referrer"
                    style={{ display: 'block', width: viewport === 'mobile' ? 390 : '100%', maxWidth: '100%', height: '62vh', minHeight: 480,
                        margin: '0 auto', border: viewport === 'mobile' ? `1px solid ${C.borderLight}` : 0, borderRadius: viewport === 'mobile' ? 12 : 9,
                        background: '#fff' }} />
            </div>
            <div style={{ marginTop: 8, fontSize: 10, color: C.dim }}>
                Scripts and forms are disabled in preview. Images referenced by relative file paths will need public URLs before sending.
            </div>
        </Modal>
    );
}

function Tabs({ tabs, active, onChange }) {
    return (
        <div className="tabs" style={{ display: 'flex', gap: 0, marginBottom: 16 }}>
            {tabs.map((t, i) => (
                <button key={t.key} onClick={() => onChange(t.key)}
                    style={{ flex: 1, padding: '10px', background: active === t.key ? C.accentDim : C.card,
                        border: `1px solid ${active === t.key ? C.accent : C.border}`,
                        borderRight: i < tabs.length - 1 ? 'none' : undefined,
                        borderRadius: i === 0 ? '8px 0 0 8px' : i === tabs.length - 1 ? '0 8px 8px 0' : 0,
                        color: active === t.key ? C.accent : C.muted, fontSize: 13, fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'inherit' }}>
                    {t.label}
                </button>
            ))}
        </div>
    );
}

// ═══════════════════════════════════════════════════════
//  LOGIN / REGISTER
// ═══════════════════════════════════════════════════════

function LoginPage() {
    const { login, register } = useAuth();
    const [isRegister, setIsRegister] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [accountName, setAccountName] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault(); setLoading(true); setError('');
        try {
            if (isRegister) { await register(email, password, name, accountName); }
            else { await login(email, password); }
        } catch (err) { setError(err.message); }
        setLoading(false);
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: C.bg }}>
            <div className="login-card" style={{ width: 420, padding: 40, background: C.card, border: `1px solid ${C.border}`, borderRadius: 16 }}>
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: `linear-gradient(135deg, ${C.accent}, #a855f7)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>⟳</div>
                        <span style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', color: C.text }}>RELAY</span>
                    </div>
                    <p style={{ color: C.muted, fontSize: 13 }}>{isRegister ? 'Create your account' : 'Sign in to your account'}</p>
                </div>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {error && <div style={{ padding: '8px 12px', borderRadius: 6, background: C.dangerDim, color: C.danger, fontSize: 12 }}>{error}</div>}
                    {isRegister && (
                        <>
                            <input style={inputStyle} placeholder="Your Name" value={name} onChange={e => setName(e.target.value)} />
                            <input style={inputStyle} placeholder="Company / Account Name" value={accountName} onChange={e => setAccountName(e.target.value)} />
                        </>
                    )}
                    <input style={inputStyle} type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required />
                    <input style={inputStyle} type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required />
                    <button type="submit" disabled={loading} style={{ ...btnPrimary, opacity: loading ? 0.6 : 1 }}>
                        {loading ? 'Please wait...' : isRegister ? 'Create Account' : 'Sign In'}
                    </button>
                </form>
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                    <button onClick={() => { setIsRegister(!isRegister); setError(''); }}
                        style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
                        {isRegister ? 'Already have an account? Sign in' : "Don't have an account? Register"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════
//  DASHBOARD
// ═══════════════════════════════════════════════════════

function DashboardPage() {
    const [data, setData] = useState(null);
    const [wsAnalytics, setWsAnalytics] = useState(null);
    useEffect(() => {
        api.getOverview().then(setData).catch(() => setData({}));
        api.getWorkspaceAnalytics().then(setWsAnalytics).catch(() => setWsAnalytics([]));
    }, []);

    if (!data) return <div style={{ color: C.muted, padding: 40 }}>Loading dashboard...</div>;
    const t = data.totals || {};
    const sent = parseInt(t.total_sent) || 0;
    const opened = parseInt(t.total_opened) || 0;
    const replied = parseInt(t.total_replied) || 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {[
                    { label: 'Emails Sent', value: sent.toLocaleString(), color: C.accent },
                    { label: 'Open Rate', value: sent ? `${((opened / sent) * 100).toFixed(1)}%` : '0%', color: C.info },
                    { label: 'Reply Rate', value: sent ? `${((replied / sent) * 100).toFixed(1)}%` : '0%', color: C.success },
                    { label: 'Active Contacts', value: (data.total_contacts || 0).toLocaleString(), color: C.warning },
                ].map(m => (
                    <div key={m.label} style={{ flex: 1, minWidth: 180, padding: '20px 24px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 12 }}>
                        <div style={{ fontSize: 12, color: C.muted, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>{m.label}</div>
                        <div style={{ fontSize: 28, fontWeight: 700, color: m.color, fontFamily: "'JetBrains Mono', monospace" }}>{m.value}</div>
                    </div>
                ))}
            </div>

            {/* Workspace Performance */}
            {wsAnalytics && wsAnalytics.length > 0 && (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600 }}>📊 Workspace Performance</h3>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {wsAnalytics.map(ws => {
                            const wsSent = parseInt(ws.total_sent) || 0;
                            const wsOpened = parseInt(ws.total_opened) || 0;
                            const wsReplies = parseInt(ws.total_replies) || 0;
                            const wsBounced = parseInt(ws.total_bounced) || 0;
                            const openPct = wsSent > 0 ? ((wsOpened / wsSent) * 100).toFixed(1) : '0.0';
                            const replyPct = wsSent > 0 ? ((wsReplies / wsSent) * 100).toFixed(1) : '0.0';
                            const bouncePct = wsSent > 0 ? ((wsBounced / wsSent) * 100).toFixed(1) : '0.0';
                            return (
                                <div key={ws.id} style={{ flex: '1 1 280px', padding: 16, background: C.hover, borderRadius: 10, border: `1px solid ${C.border}` }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{ws.display_name || ws.email}</div>
                                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>{ws.provider} — {ws.email}</div>
                                    <div style={{ display: 'flex', gap: 16 }}>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'monospace' }}>{wsSent}</div>
                                            <div style={{ fontSize: 10, color: C.muted }}>SENT</div>
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'monospace', color: parseFloat(openPct) > 30 ? C.success : parseFloat(openPct) > 15 ? C.info : C.warning }}>{openPct}%</div>
                                            <div style={{ fontSize: 10, color: C.muted }}>OPENS</div>
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'monospace', color: C.success }}>{replyPct}%</div>
                                            <div style={{ fontSize: 10, color: C.muted }}>REPLIES</div>
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'monospace', color: parseFloat(bouncePct) > 5 ? C.danger : C.dim }}>{bouncePct}%</div>
                                            <div style={{ fontSize: 10, color: C.muted }}>BOUNCED</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600 }}>Active Workspaces</h3>
                    {(data.workspaces || []).map(ws => (
                        <div key={ws.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
                            <HealthRing value={ws.health_score || 0} size={40} />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 600 }}>{ws.display_name || ws.email}</div>
                                <div style={{ fontSize: 11, color: C.muted }}>{ws.provider || 'gmail'} — {ws.email}</div>
                            </div>
                            <div style={{ fontFamily: 'monospace', fontSize: 12 }}>{ws.daily_sent_count}/{ws.daily_send_limit}</div>
                        </div>
                    ))}
                    {(!data.workspaces || data.workspaces.length === 0) && (
                        <div style={{ textAlign: 'center', padding: 20, color: C.dim }}>
                            <Link to="/workspaces" style={{ color: C.accent, fontSize: 13 }}>Connect a workspace →</Link>
                        </div>
                    )}
                </div>
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600 }}>Quick Actions</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {[
                            { to: '/campaigns/new', label: '✏ New Campaign', accent: true },
                            { to: '/contacts', label: '📥 Import Contacts' },
                            { to: '/workspaces', label: '🔗 Connect Workspace' },
                            { to: '/account', label: '⚙ Account Settings' },
                        ].map(a => (
                            <Link key={a.to} to={a.to} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                                background: a.accent ? C.accentDim : C.hover, border: `1px solid ${a.accent ? C.accent : C.border}`,
                                borderRadius: 8, color: a.accent ? C.accent : C.text, textDecoration: 'none', fontSize: 13, fontWeight: a.accent ? 600 : 400 }}>
                                {a.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════
//  LIST & SUBSCRIBER ANALYTICS
// ═══════════════════════════════════════════════════════

function AudienceAnalyticsPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({ search: '', status: 'all', engagement: 'all', list_id: '' });

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const params = { page, limit: 25 };
            Object.entries(filters).forEach(([key, value]) => { if (value && value !== 'all') params[key] = value; });
            setData(await api.getAudienceAnalytics(params));
        } catch (err) { setError(err.message); }
        setLoading(false);
    }, [filters, page]);

    useEffect(() => {
        const timer = setTimeout(load, filters.search ? 250 : 0);
        return () => clearTimeout(timer);
    }, [load, filters.search]);

    const updateFilter = (key, value) => { setPage(1); setFilters(current => ({ ...current, [key]: value })); };
    const number = value => Number(value || 0).toLocaleString();
    const percent = value => value === null || value === undefined ? '—' : `${Number(value).toFixed(1)}%`;
    const summary = data?.summary || {};
    const pagination = data?.pagination || { page: 1, pages: 1, total: 0 };
    const metrics = [
        ['Total contacts', summary.total_contacts, C.text, 'All contacts in this account'],
        ['Active contacts', summary.active_contacts, C.success, 'Currently eligible contacts'],
        ['Unique openers', summary.unique_openers, C.info, `${percent(summary.unique_opener_rate_pct)} of reached contacts`],
        ['Repeat openers', summary.repeat_openers, '#a78bfa', 'Opened across 2+ campaigns'],
        ['Unique clickers', summary.unique_clickers, C.accent, `${percent(summary.unique_clicker_rate_pct)} of reached contacts`],
        ['Unsubscribed', summary.unsubscribed_contacts, C.warning, `${number(summary.unsubscribed_openers)} had opened`],
    ];

    if (!data && loading) return <div style={{ color: C.muted, padding: 40 }}>Loading list analytics…</div>;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'start', flexWrap: 'wrap' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 22 }}>List & Subscriber Analytics</h2>
                    <p style={{ margin: '5px 0 0', color: C.muted, fontSize: 12 }}>
                        Person-level engagement with bot-adjusted opens and clicks. “Subscriber” reflects active vs. unsubscribed email status.
                    </p>
                </div>
                <button onClick={load} disabled={loading} style={{ ...btnSecondary, padding: '8px 13px', opacity: loading ? 0.6 : 1 }}>
                    {loading ? 'Refreshing…' : '↻ Refresh'}
                </button>
            </div>

            {error && <div style={{ padding: 12, background: C.dangerDim, color: C.danger, border: `1px solid ${C.danger}`, borderRadius: 8 }}>{error}</div>}

            <div className="audience-metric-grid">
                {metrics.map(([label, value, color, note]) => (
                    <div key={label} style={{ padding: 17, background: C.card, border: `1px solid ${C.border}`, borderRadius: 11 }}>
                        <div style={{ color: C.muted, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</div>
                        <div style={{ color, fontSize: 27, fontWeight: 700, fontFamily: 'monospace', marginTop: 5 }}>{number(value)}</div>
                        <div style={{ color: C.dim, fontSize: 10, marginTop: 3 }}>{note}</div>
                    </div>
                ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(260px, 1fr)', gap: 16 }}>
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{ padding: '16px 18px 12px' }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>List performance</div>
                        <div style={{ color: C.muted, fontSize: 10, marginTop: 3 }}>Engagement is attributed to campaigns sent from each list.</div>
                    </div>
                    <div className="audience-table-wrap">
                        <table className="audience-table">
                            <thead><tr><th>List</th><th>Members</th><th>Reached</th><th>Openers</th><th>Clickers</th><th>Unsubs</th></tr></thead>
                            <tbody>
                                {(data?.lists || []).map(list => (
                                    <tr key={list.id} onClick={() => updateFilter('list_id', list.id)} style={{ cursor: 'pointer' }}>
                                        <td><strong>{list.name}</strong><small>{number(list.emails_sent)} emails sent</small></td>
                                        <td>{number(list.members)}</td>
                                        <td>{number(list.reached)}</td>
                                        <td><span style={{ color: C.info }}>{number(list.unique_openers)}</span><small>{percent(list.opener_reach_pct)}</small></td>
                                        <td><span style={{ color: C.accent }}>{number(list.unique_clickers)}</span><small>{percent(list.clicker_reach_pct)}</small></td>
                                        <td style={{ color: Number(list.unsubscribed_members) ? C.warning : C.muted }}>{number(list.unsubscribed_members)}</td>
                                    </tr>
                                ))}
                                {!data?.lists?.length && <tr><td colSpan="6" style={{ color: C.muted, textAlign: 'center', padding: 24 }}>No contact lists yet.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14 }}>Audience health</div>
                    {[
                        ['Reached at least once', summary.reached_contacts, C.accent],
                        ['Never opened after reach', summary.reached_not_opened, C.dim],
                        ['Strict human openers', summary.strict_human_openers, C.success],
                        ['Contacts with bounces', summary.contacts_with_bounces, C.danger],
                    ].map(([label, value, color]) => (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${C.border}`, fontSize: 12 }}>
                            <span style={{ color: C.muted }}>{label}</span><strong style={{ color, fontFamily: 'monospace' }}>{number(value)}</strong>
                        </div>
                    ))}
                    <div style={{ marginTop: 14, padding: 11, borderRadius: 8, background: C.infoDim, color: C.info, fontSize: 10, lineHeight: 1.5 }}>
                        Clean openers include privacy-proxy opens after bot removal. Strict human openers exclude privacy proxies.
                    </div>
                </div>
            </div>

            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                <div className="audience-filters">
                    <div><strong style={{ fontSize: 14 }}>Recipients</strong><div style={{ color: C.muted, fontSize: 10, marginTop: 2 }}>{number(pagination.total)} matching contacts</div></div>
                    <input style={{ ...inputStyle, width: 230, padding: '8px 11px' }} placeholder="Search email, name or company" value={filters.search} onChange={e => updateFilter('search', e.target.value)} />
                    <select style={{ ...inputStyle, width: 160, padding: '8px 11px' }} value={filters.list_id} onChange={e => updateFilter('list_id', e.target.value)}>
                        <option value="">All lists</option>{(data?.lists || []).map(list => <option key={list.id} value={list.id}>{list.name}</option>)}
                    </select>
                    <select style={{ ...inputStyle, width: 145, padding: '8px 11px' }} value={filters.status} onChange={e => updateFilter('status', e.target.value)}>
                        <option value="all">All statuses</option><option value="active">Active</option><option value="unsubscribed">Unsubscribed</option>
                    </select>
                    <select style={{ ...inputStyle, width: 170, padding: '8px 11px' }} value={filters.engagement} onChange={e => updateFilter('engagement', e.target.value)}>
                        <option value="all">All engagement</option><option value="opened">Opened</option><option value="repeat">Repeat openers</option><option value="clicked">Clicked</option><option value="unengaged">Reached, no open</option>
                    </select>
                </div>
                <div className="audience-table-wrap">
                    <table className="audience-table audience-recipient-table">
                        <thead><tr><th>Recipient</th><th>Status</th><th>Sent</th><th>Campaigns opened</th><th>Clicks</th><th>Open rate</th><th>Last clean open</th></tr></thead>
                        <tbody>
                            {(data?.recipients || []).map(recipient => (
                                <tr key={recipient.id}>
                                    <td><strong>{recipient.email}</strong><small>{[recipient.first_name, recipient.last_name].filter(Boolean).join(' ') || recipient.company || '—'}</small></td>
                                    <td><StatusBadge status={recipient.status} /></td>
                                    <td>{number(recipient.emails_sent)}</td>
                                    <td title={(recipient.clean_opened_campaigns || []).join('\n')}><span style={{ color: Number(recipient.campaigns_clean_opened) >= 2 ? '#a78bfa' : C.info }}>{number(recipient.campaigns_clean_opened)}</span><small>{number(recipient.emails_clean_opened)} emails</small></td>
                                    <td>{number(recipient.emails_clean_clicked)}</td>
                                    <td>{percent(recipient.receiver_clean_open_rate_pct)}</td>
                                    <td>{recipient.last_clean_open_at ? new Date(recipient.last_clean_open_at).toLocaleDateString() : '—'}</td>
                                </tr>
                            ))}
                            {!data?.recipients?.length && <tr><td colSpan="7" style={{ textAlign: 'center', color: C.muted, padding: 28 }}>No recipients match these filters.</td></tr>}
                        </tbody>
                    </table>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 13, borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.muted }}>
                    <span>Page {pagination.page} of {Math.max(pagination.pages, 1)}</span>
                    <div style={{ display: 'flex', gap: 7 }}>
                        <button style={{ ...btnSecondary, padding: '6px 11px' }} disabled={page <= 1} onClick={() => setPage(value => Math.max(1, value - 1))}>Previous</button>
                        <button style={{ ...btnSecondary, padding: '6px 11px' }} disabled={page >= pagination.pages} onClick={() => setPage(value => value + 1)}>Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function OperationsPage() {
    const [overview,setOverview]=useState(null),[timeline,setTimeline]=useState([]),[tab,setTab]=useState('health'),[expandedDomain,setExpandedDomain]=useState(null);
    const load=useCallback(async()=>{ try { const [o,t]=await Promise.all([api.getOperationsOverview(),api.getOperationsTimeline({limit:100})]); setOverview(o);setTimeline(t.items||[]); } catch(e){ setOverview({error:e.message}); } },[]);
    useEffect(()=>{
        load(); let stopped=false, fallback=setInterval(load,30000), controller=new AbortController();
        (async()=>{ try { const headers={Authorization:`Bearer ${localStorage.getItem('relay_token')}`,'X-Account-Id':localStorage.getItem('relay_account_id')||''};
            const res=await fetch('/api/operations/stream',{headers,signal:controller.signal}); const reader=res.body.getReader(),decoder=new TextDecoder();
            while(!stopped){const {done,value}=await reader.read();if(done)break;if(decoder.decode(value).includes('event: reconcile'))load();}
        }catch(e){ if(!stopped) load(); }})();
        return()=>{stopped=true;controller.abort();clearInterval(fallback);};
    },[load]);
    if(!overview) return <div style={{color:C.muted,padding:40}}>Loading sending operations…</div>;
    if(overview.error) return <div style={{color:C.danger,padding:40}}>{overview.error}</div>;
    const s=overview.summary||{};
    const pct=(value,total)=>total ? `${((Number(value||0)/Number(total))*100).toFixed(1)}%` : '—';
    const number=value=>Number(value||0).toLocaleString();
    const healthColor=value=>Number(value||0)>90?C.success:Number(value||0)>75?C.warning:C.danger;
    const action=async(id,type)=>{ if(!window.confirm(`${type} this send?`))return; await (type==='Retry'?api.retrySend(id):api.cancelSend(id));load(); };
    return <div style={{display:'flex',flexDirection:'column',gap:18}}>
        <div style={{display:'flex',justifyContent:'space-between',gap:16,alignItems:'start',flexWrap:'wrap'}}><div><h2 style={{margin:0}}>Sending Operations</h2><div style={{fontSize:12,color:C.muted}}>Domain health and sender traffic control · updated {new Date(overview.server_time).toLocaleTimeString()}</div></div><button onClick={load} style={{...btnSecondary,padding:'8px 12px'}}>Refresh now</button></div>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>{[
            ['Sent today',s.sent_today,C.success],['Scheduled today',s.scheduled_today,C.accent],['In queue',s.planned,C.info],['Deferred',s.deferred,C.warning],['Failed today',s.failed_today,C.danger],['CTR',pct(s.clicks,s.total_sent),C.info]
        ].map(([label,value,color])=><div key={label} style={{minWidth:135,flex:1,background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}><div style={{fontSize:11,color:C.muted,textTransform:'uppercase'}}>{label}</div><div style={{fontSize:26,fontWeight:700,color}}>{typeof value==='string'?value:parseInt(value)||0}</div></div>)}</div>
        <div style={{display:'flex',gap:6}}>{[['health','Domain health'],['flow','Campaign flow'],['schedule','Traffic & schedule'],['activity','Live activity']].map(([id,label])=><button key={id} onClick={()=>setTab(id)} style={{...btnPrimary,padding:'8px 14px',background:tab===id?C.accent:C.hover,border:`1px solid ${C.border}`}}>{label}</button>)}</div>
        {tab==='health'&&<>
            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden'}}><div style={{padding:16}}><b>Domain health overview</b><div style={{fontSize:11,color:C.muted,marginTop:3}}>Start at the domain level; select a row to inspect its inboxes.</div></div>{(overview.domains||[]).map(d=>{const open=expandedDomain===d.domain, inboxes=(overview.inboxes||[]).filter(i=>(i.domain||'Unassigned')===d.domain);return <div key={d.domain} style={{borderTop:`1px solid ${C.border}`}}><button onClick={()=>setExpandedDomain(open?null:d.domain)} style={{width:'100%',display:'grid',gridTemplateColumns:'minmax(190px,1.5fr) 90px 100px 90px 90px 100px 28px',gap:12,padding:'14px 16px',background:open?C.hover:'transparent',border:0,color:C.text,textAlign:'left',cursor:'pointer',fontFamily:'inherit',fontSize:12,alignItems:'center'}}><span><b>{d.domain}</b><br/><small style={{color:C.muted}}>{d.inbox_count} inbox{Number(d.inbox_count)===1?'':'es'} · {number(d.total_sent)} lifetime sends</small></span><span style={{color:healthColor(d.health_score),fontWeight:700}}>{d.health_score||0}/100</span><span>{number(d.sent_today)}<br/><small style={{color:C.muted}}>sent today</small></span><span style={{color:C.info}}>{pct(d.clicks,d.total_sent)}<br/><small style={{color:C.muted}}>CTR</small></span><span style={{color:Number(d.bounces)>0?C.warning:C.success}}>{pct(d.bounces,d.total_sent)}<br/><small style={{color:C.muted}}>bounce</small></span><span style={{color:Number(d.spam_reports)>0?C.danger:C.success}}>{pct(d.spam_reports,d.total_sent)}<br/><small style={{color:C.muted}}>spam</small></span><span style={{color:C.muted,fontSize:18}}>{open?'⌃':'⌄'}</span></button>{open&&<div style={{background:'#0e0e15',padding:'0 16px 14px 40px'}}><div style={{display:'grid',gridTemplateColumns:'minmax(180px,1.4fr) 90px 100px 90px 90px 100px',gap:10,padding:'10px 0',fontSize:10,color:C.muted,textTransform:'uppercase'}}><span>Inbox</span><span>Health</span><span>Today</span><span>Sent</span><span>CTR</span><span>Bounce / spam</span></div>{inboxes.map(i=><div key={i.id} style={{display:'grid',gridTemplateColumns:'minmax(180px,1.4fr) 90px 100px 90px 90px 100px',gap:10,padding:'12px 0',borderTop:`1px solid ${C.border}`,fontSize:12,alignItems:'center'}}><span><b>{i.email}</b><br/><small style={{color:C.muted}}>{i.status}</small></span><span style={{color:healthColor(i.health_score),fontWeight:700}}>{i.health_score||0}/100</span><span>{number(i.sent_today)} sent<br/><small style={{color:C.muted}}>{number(i.scheduled_today)} queued</small></span><span>{number(i.total_sent)}</span><span style={{color:C.info}}>{pct(i.clicks,i.total_sent)}</span><span><span style={{color:Number(i.bounces)>0?C.warning:C.success}}>{pct(i.bounces,i.total_sent)}</span><br/><small style={{color:Number(i.spam_reports)>0?C.danger:C.muted}}>spam {pct(i.spam_reports,i.total_sent)}</small></span></div>)}</div>}</div>})}{!overview.domains?.length&&<div style={{padding:16,color:C.muted}}>Connect inboxes to begin monitoring domain health.</div>}</div>
        </>}
        {tab==='flow'&&<CampaignFlow overview={overview} refreshSignal={overview.server_time}/>}
        {tab==='schedule'&&<>
            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}><div style={{display:'flex',justifyContent:'space-between',gap:12,alignItems:'start',marginBottom:16}}><div><b>Sender traffic horizon</b><div style={{fontSize:11,color:C.muted,marginTop:3}}>Recent sent volume, today’s live throughput, and the next seven days of scheduled traffic.</div></div><div style={{display:'flex',gap:10,fontSize:11,color:C.muted}}><span><i style={{display:'inline-block',width:8,height:8,borderRadius:2,background:C.success,marginRight:4}}/>sent</span><span><i style={{display:'inline-block',width:8,height:8,borderRadius:2,background:C.accent,marginRight:4}}/>scheduled</span></div></div><div style={{display:'grid',gridTemplateColumns:'repeat(15,minmax(38px,1fr))',gap:8,alignItems:'end',minHeight:130}}>{(overview.traffic||[]).map(t=>{const max=Math.max(1,...(overview.traffic||[]).map(x=>Number(x.sent)+Number(x.scheduled))), sent=Math.round((Number(t.sent)/max)*86), scheduled=Math.round((Number(t.scheduled)/max)*86), day=new Date(t.day), today=day.toDateString()===new Date().toDateString(), future=day>new Date();return <div key={t.day} title={`${day.toLocaleDateString()}: ${t.sent} sent, ${t.scheduled} scheduled`} style={{minWidth:0,textAlign:'center'}}><div style={{height:96,display:'flex',alignItems:'end',justifyContent:'center',gap:3,borderBottom:`1px solid ${today?C.accent:C.border}`}}><div style={{width:'40%',height:sent,background:C.success,borderRadius:'3px 3px 0 0'}}/><div style={{width:'40%',height:scheduled,background:C.accent,borderRadius:'3px 3px 0 0'}}/></div><div style={{fontSize:10,color:today?C.accent:future?C.text:C.muted,fontWeight:today?700:400,marginTop:7}}>{today?'Today':day.toLocaleDateString(undefined,{weekday:'short'})}</div><div style={{fontSize:10,color:C.muted}}>{number(t.sent||0)}/{number(t.scheduled||0)}</div></div>})}</div><div style={{fontSize:10,color:C.muted,marginTop:10}}>Each day shows sent / scheduled. Scheduled traffic begins today; past days show delivered volume.</div></div>
            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden'}}><div style={{padding:16}}><b>Inbox scheduled workload</b><div style={{fontSize:11,color:C.muted,marginTop:3}}>The plan shown here is persisted by the scheduler and refreshes automatically.</div></div>{(overview.inboxes||[]).map(i=>{const capacity=Math.max(Number(i.per_day_cap||12),1), used=Number(i.used_today||0), queued=Number(i.scheduled_today||0), loadPct=Math.min(100,Math.round((used/capacity)*100));return <div key={i.id} style={{display:'grid',gridTemplateColumns:'minmax(190px,1.4fr) 1fr 90px 130px',gap:16,padding:'13px 16px',borderTop:`1px solid ${C.border}`,fontSize:12,alignItems:'center'}}><span><b>{i.email}</b><br/><small style={{color:C.muted}}>{i.domain||'Unassigned domain'} · next {i.next_send?new Date(i.next_send).toLocaleString():'send not assigned'}</small></span><span><div style={{height:7,background:C.active,borderRadius:9,overflow:'hidden'}}><div style={{width:`${loadPct}%`,height:'100%',background:loadPct>90?C.danger:loadPct>70?C.warning:C.success}}/></div><small style={{color:C.muted}}>{used}/{capacity} daily capacity allocated</small></span><span><b>{queued}</b><br/><small style={{color:C.muted}}>today</small></span><span><b>{number(i.scheduled_next_7_days)}</b><br/><small style={{color:C.muted}}>next 7 days</small></span></div>})}</div>
            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}><h3 style={{margin:'0 0 12px'}}>Seven-day send plan</h3>{(overview.forecast||[]).map((f,i)=><div key={`${f.day}-${f.status}-${i}`} style={{display:'flex',justifyContent:'space-between',padding:'9px 0',borderBottom:`1px solid ${C.border}`,fontSize:12}}><span>{new Date(f.day).toLocaleDateString()}</span><span style={{color:C.muted}}>{f.status}</span><b>{number(f.count)}</b></div>)}{!overview.forecast?.length&&<div style={{color:C.muted}}>No planned sends in the next seven days.</div>}</div>
        </>}
        {tab==='activity'&&<>
            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden'}}><div style={{padding:16,fontWeight:600}}>Send timeline</div>{timeline.map(r=><div key={r.id} style={{display:'grid',gridTemplateColumns:'120px 1.2fr 1fr 1fr 100px',gap:8,padding:'10px 16px',borderTop:`1px solid ${C.border}`,fontSize:12,alignItems:'center'}}><span style={{color:C.accent}}>{r.status}</span><span>{r.recipient}</span><span>{r.campaign}</span><span>{r.inbox||'Unassigned'}<br/><small style={{color:C.muted}}>{r.scheduled_at?new Date(r.scheduled_at).toLocaleString():''}</small></span><span>{['failed','deferred'].includes(r.status)&&<button onClick={()=>action(r.id,'Retry')} style={{...btnPrimary,padding:'5px 8px'}}>Retry</button>}{['pending','planned','scheduled','deferred'].includes(r.status)&&<button onClick={()=>action(r.id,'Cancel')} style={{background:'none',border:0,color:C.danger,cursor:'pointer'}}>Cancel</button>}</span></div>)}</div>
        </>}
    </div>;
}

// ═══════════════════════════════════════════════════════
//  WORKSPACES — with all provider forms
// ═══════════════════════════════════════════════════════

function WorkspacesPage() {
    const [workspaces, setWorkspaces] = useState([]);
    const [toast, setToast] = useState(null);
    const [showAdd, setShowAdd] = useState(null); // null, 'smtp', 'sendgrid', 'mailgun', 'ses'
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [searchParams] = useSearchParams();

    const [editWs, setEditWs] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [avatarSaving, setAvatarSaving] = useState(false);

    const load = useCallback(() => { api.getWorkspaces().then(setWorkspaces).catch(() => {}); }, []);
    useEffect(load, [load]);
    useEffect(() => {
        const connected = searchParams.get('connected');
        if (connected) setToast({ message: `Connected: ${connected}`, type: 'success' });
        const error = searchParams.get('error');
        if (error) setToast({ message: `Error: ${error}`, type: 'error' });
    }, [searchParams]);

    const connectGoogle = async () => {
        try { const { authUrl } = await api.getConnectUrl(); window.location.href = authUrl; }
        catch (err) { setToast({ message: err.message, type: 'error' }); }
    };

    const addWorkspace = async () => {
        setSaving(true);
        try {
            if (showAdd === 'smtp') await api.addSmtpWorkspace(form);
            if (showAdd === 'sendgrid') await api.addSendgridWorkspace(form);
            if (showAdd === 'mailgun') await api.addMailgunWorkspace(form);
            if (showAdd === 'resend') await api.addResendWorkspace(form);
            if (showAdd === 'ses') await api.addSesWorkspace(form);
            setToast({ message: 'Workspace added!', type: 'success' });
            setShowAdd(null); setForm({}); load();
        } catch (err) { setToast({ message: err.message, type: 'error' }); }
        setSaving(false);
    };

    const uploadAvatar = async (file) => {
        if (!file) return;
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            setToast({ message: 'Choose a JPG, PNG, or WebP image', type: 'error' });
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setToast({ message: 'Avatar must be 5 MB or smaller', type: 'error' });
            return;
        }

        setAvatarSaving(true);
        try {
            const updated = await api.uploadWorkspaceAvatar(editWs.id, file);
            setEditWs(current => current ? ({ ...current, profile_image_url: updated.profile_image_url }) : current);
            setToast({ message: 'Workspace image updated', type: 'success' });
            load();
        } catch (err) {
            setToast({ message: err.message, type: 'error' });
        } finally {
            setAvatarSaving(false);
        }
    };

    const removeAvatar = async () => {
        setAvatarSaving(true);
        try {
            await api.removeWorkspaceAvatar(editWs.id);
            setEditWs(current => current ? ({ ...current, profile_image_url: null }) : current);
            setToast({ message: 'Workspace image removed', type: 'success' });
            load();
        } catch (err) {
            setToast({ message: err.message, type: 'error' });
        } finally {
            setAvatarSaving(false);
        }
    };

    const F = (key, placeholder, type = 'text') => (
        <div>
            <label style={labelStyle}>{placeholder}</label>
            <input style={inputStyle} type={type} placeholder={placeholder} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
        </div>
    );

    return (
        <div>
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}

            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
                <button onClick={connectGoogle} style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', gap: 6 }}>📧 Gmail (OAuth)</button>
                <button onClick={() => { setShowAdd('smtp'); setForm({}); }} style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6 }}>📮 SMTP / IMAP</button>
                <button onClick={() => { setShowAdd('sendgrid'); setForm({}); }} style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6 }}>⚡ SendGrid</button>
                <button onClick={() => { setShowAdd('mailgun'); setForm({}); }} style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6 }}>🔫 Mailgun</button>
                <button onClick={() => { setShowAdd('resend'); setForm({}); }} style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6 }}>✉ Resend</button>
                <button onClick={() => { setShowAdd('ses'); setForm({}); }} style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6 }}>☁ Amazon SES</button>
            </div>

            {/* Provider add modals */}
            {showAdd === 'smtp' && (
                <Modal title="Add SMTP Workspace" onClose={() => setShowAdd(null)}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {F('email', 'Email Address')} {F('display_name', 'Display Name')}
                        <div style={{ display: 'flex', gap: 12 }}>
                            <div style={{ flex: 2 }}>{F('smtp_host', 'SMTP Host (e.g. smtp.office365.com)')}</div>
                            <div style={{ flex: 1 }}>{F('smtp_port', 'Port', 'number')}</div>
                        </div>
                        {F('smtp_user', 'SMTP Username (usually your email)')} {F('smtp_pass', 'SMTP Password / App Password', 'password')}
                        <div style={{ padding: 12, background: C.hover, borderRadius: 8, fontSize: 12, color: C.muted }}>
                            <strong>IMAP (optional)</strong> — for reply detection
                        </div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <div style={{ flex: 2 }}>{F('imap_host', 'IMAP Host (e.g. imap.office365.com)')}</div>
                            <div style={{ flex: 1 }}>{F('imap_port', 'Port', 'number')}</div>
                        </div>
                        {F('daily_send_limit', 'Daily Send Limit', 'number')}
                        <button onClick={addWorkspace} disabled={saving} style={{ ...btnPrimary, marginTop: 8 }}>{saving ? 'Adding...' : 'Add SMTP Workspace'}</button>
                    </div>
                </Modal>
            )}
            {showAdd === 'sendgrid' && (
                <Modal title="Add SendGrid Workspace" onClose={() => setShowAdd(null)}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {F('api_key', 'SendGrid API Key', 'password')} {F('email', 'From Email')} {F('display_name', 'From Name')}
                        {F('daily_send_limit', 'Daily Send Limit', 'number')}
                        <button onClick={addWorkspace} disabled={saving} style={{ ...btnPrimary, marginTop: 8 }}>{saving ? 'Adding...' : 'Add SendGrid Workspace'}</button>
                    </div>
                </Modal>
            )}
            {showAdd === 'mailgun' && (
                <Modal title="Add Mailgun Workspace" onClose={() => setShowAdd(null)}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {F('api_key', 'Mailgun API Key', 'password')} {F('mg_domain', 'Mailgun Domain')}
                        {F('email', 'From Email')} {F('display_name', 'From Name')}
                        <div>
                            <label style={labelStyle}>Region</label>
                            <select style={inputStyle} value={form.region || 'us'} onChange={e => setForm(f => ({ ...f, region: e.target.value }))}>
                                <option value="us">US</option><option value="eu">EU</option>
                            </select>
                        </div>
                        <button onClick={addWorkspace} disabled={saving} style={{ ...btnPrimary, marginTop: 8 }}>{saving ? 'Adding...' : 'Add Mailgun Workspace'}</button>
                    </div>
                </Modal>
            )}
            {showAdd === 'resend' && (
                <Modal title="Add Resend Workspace" onClose={() => setShowAdd(null)}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {F('api_key', 'Resend API Key', 'password')}
                        {F('email', 'Verified From Email')}
                        {F('display_name', 'From Name')}
                        {F('daily_send_limit', 'Daily Send Limit', 'number')}
                        <button onClick={addWorkspace} disabled={saving} style={{ ...btnPrimary, marginTop: 8 }}>{saving ? 'Adding...' : 'Add Resend Workspace'}</button>
                    </div>
                </Modal>
            )}
            {showAdd === 'ses' && (
                <Modal title="Add Amazon SES Workspace" onClose={() => setShowAdd(null)}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {F('access_key_id', 'AWS Access Key ID')} {F('secret_access_key', 'AWS Secret Access Key', 'password')}
                        {F('ses_region', 'SES Region (e.g. us-east-1)')} {F('email', 'Verified Sender Email')} {F('display_name', 'From Name')}
                        <button onClick={addWorkspace} disabled={saving} style={{ ...btnPrimary, marginTop: 8 }}>{saving ? 'Adding...' : 'Add SES Workspace'}</button>
                    </div>
                </Modal>
            )}

            {editWs && (
                <Modal title={`Edit: ${editWs.display_name || editWs.email}`} onClose={() => setEditWs(null)}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div>
                            <label style={labelStyle}>RELAY Workspace Image</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 12, border: `1px solid ${C.border}`, borderRadius: 10, background: C.hover }}>
                                <WorkspaceAvatar workspace={editWs} size={64} />
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <label style={{ ...btnSecondary, padding: '7px 12px', fontSize: 11, opacity: avatarSaving ? 0.6 : 1, pointerEvents: avatarSaving ? 'none' : 'auto' }}>
                                        {avatarSaving ? 'Updating…' : 'Choose Image'}
                                        <input
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp"
                                            disabled={avatarSaving}
                                            onChange={event => {
                                                const file = event.target.files?.[0];
                                                event.target.value = '';
                                                uploadAvatar(file);
                                            }}
                                            style={{ display: 'none' }}
                                        />
                                    </label>
                                    {editWs.profile_image_url && (
                                        <button type="button" onClick={removeAvatar} disabled={avatarSaving}
                                            style={{ ...btnSecondary, padding: '7px 12px', fontSize: 11, color: C.danger, borderColor: C.danger, opacity: avatarSaving ? 0.6 : 1 }}>
                                            Remove
                                        </button>
                                    )}
                                    <div style={{ flexBasis: '100%', fontSize: 10, color: C.dim }}>
                                        JPG, PNG, or WebP · maximum 5 MB · changes RELAY only, not the Google account photo
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div><label style={labelStyle}>Display Name</label>
                            <input style={inputStyle} value={editForm.display_name || ''} onChange={e => setEditForm(f => ({ ...f, display_name: e.target.value }))} /></div>
                        <div><label style={labelStyle}>Daily Send Limit</label>
                            <input style={inputStyle} type="number" value={editForm.daily_send_limit || ''} onChange={e => setEditForm(f => ({ ...f, daily_send_limit: parseInt(e.target.value) || 0 }))} /></div>
                        <div><label style={labelStyle}>Tracking Domain</label>
                            <input style={inputStyle} value={editForm.tracking_domain || ''} onChange={e => setEditForm(f => ({ ...f, tracking_domain: e.target.value }))} placeholder="e.g. trk.yourdomain.com" />
                            <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>Point a CNAME to your tracking server. Each workspace can use a different domain to isolate reputation.</div></div>
                        <button onClick={async () => {
                            try { await api.updateWorkspace(editWs.id, editForm); setToast({ message: 'Workspace updated', type: 'success' }); setEditWs(null); load(); }
                            catch (e) { setToast({ message: e.message, type: 'error' }); }
                        }} style={{ ...btnPrimary, marginTop: 8 }}>Save Changes</button>
                    </div>
                </Modal>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
                {workspaces.map(ws => (
                    <div key={ws.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                                <WorkspaceAvatar workspace={ws} />
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ws.display_name || ws.email}</div>
                                    <div style={{ fontSize: 12, color: C.muted }}>{ws.provider?.toUpperCase()} — {ws.email}</div>
                                    {ws.tracking_domain && <div style={{ fontSize: 11, color: C.dim }}>🔗 {ws.tracking_domain}</div>}
                                </div>
                            </div>
                            <StatusBadge status={ws.status} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                            <HealthRing value={ws.health_score || 0} size={52} />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Daily Usage</div>
                                <div style={{ height: 8, background: C.active, borderRadius: 4, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', borderRadius: 4, width: `${Math.min(100, (ws.daily_sent_count / ws.daily_send_limit) * 100)}%`,
                                        background: ws.daily_sent_count / ws.daily_send_limit > 0.8 ? C.warning : C.accent }} />
                                </div>
                                <div style={{ fontSize: 11, color: C.muted, marginTop: 4, fontFamily: 'monospace' }}>{ws.daily_sent_count} / {ws.daily_send_limit}</div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => { setEditWs(ws); setEditForm({ display_name: ws.display_name || '', daily_send_limit: ws.daily_send_limit || 500, tracking_domain: ws.tracking_domain || '' }); }}
                                style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11, flex: 1 }}>Edit</button>
                            <button onClick={async () => { try { await api.checkHealth(ws.id); load(); setToast({ message: 'Health check done', type: 'info' }); } catch(e) { setToast({ message: e.message, type: 'error' }); }}}
                                style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11, flex: 1 }}>Check Health</button>
                            <button onClick={async () => { if (window.confirm('Disconnect this workspace?')) { await api.deleteWorkspace(ws.id); load(); }}}
                                style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11, color: C.danger, borderColor: C.danger }}>Disconnect</button>
                        </div>
                    </div>
                ))}
            </div>

            {workspaces.length === 0 && (
                <div style={{ textAlign: 'center', padding: 60, color: C.dim, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12 }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>🔗</div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>No workspaces connected</div>
                    <div style={{ fontSize: 13, marginTop: 8, color: C.muted }}>Use the buttons above to connect Gmail, SMTP, SendGrid, Mailgun, or Amazon SES</div>
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════
//  CAMPAIGN EDITOR — with HTML/text modes
// ═══════════════════════════════════════════════════════

function CampaignEditorPage() {
    const navigate = useNavigate();
    const { id: editId } = useParams();
    const [step, setStep] = useState(1);
    const [name, setName] = useState('');
    const [subject, setSubject] = useState('');
    const [fromName, setFromName] = useState('');
    const [format, setFormat] = useState('html'); // html, text, both
    const [blocks, setBlocks] = useState([createBlock('header'), createBlock('text'), createBlock('button')]);
    const [htmlBody, setHtmlBody] = useState('');
    const [textBody, setTextBody] = useState('');
    const [rawHtml, setRawHtml] = useState('');
    const [editMode, setEditMode] = useState('designer'); // designer, html, text
    const [workspaces, setWorkspaces] = useState([]);
    const [selectedWS, setSelectedWS] = useState([]);
    const [selectedDomains,setSelectedDomains]=useState([]);
    const [lists, setLists] = useState([]);
    const [selectedList, setSelectedList] = useState('');
    const [rotation, setRotation] = useState('round-robin');
    const [pace, setPace] = useState('normal');
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);
    const [loaded, setLoaded] = useState(!editId);
    const [loadError, setLoadError] = useState('');
    const [scheduleAt, setScheduleAt] = useState('');
    const [maxPerHour, setMaxPerHour] = useState('');
    const [contactCount, setContactCount] = useState(null);
    const [timezone, setTimezone] = useState('');
    const [scheduleProfileId,setScheduleProfileId]=useState('');
    const [schedulePreset,setSchedulePreset]=useState('workday');
    const [campaignDailyLimit,setCampaignDailyLimit]=useState(12);
    const [scheduleTimezone,setScheduleTimezone]=useState('America/New_York');
    const [weekdayWindows,setWeekdayWindows]=useState([{start:'09:00',end:'17:00'}]);
    const [includeWeekdays,setIncludeWeekdays]=useState(true);
    const [includeWeekends,setIncludeWeekends]=useState(false);
    const [weekendUseWeekdayHours,setWeekendUseWeekdayHours]=useState(true);
    const [weekendWindows,setWeekendWindows]=useState([{start:'10:00',end:'16:00'}]);
    const [minimumSpacing,setMinimumSpacing]=useState(20);
    const [scheduleTab,setScheduleTab]=useState('weekdays');
    // A/B Testing
    const [abEnabled, setAbEnabled] = useState(false);
    const [abSubjectB, setAbSubjectB] = useState('');
    const [abHtmlBodyB, setAbHtmlBodyB] = useState('');
    const [abTextBodyB, setAbTextBodyB] = useState('');
    const [abSplitPct, setAbSplitPct] = useState(50);
    const [abWinnerMetric, setAbWinnerMetric] = useState('opens');
    // Test send
    const [testEmail, setTestEmail] = useState('');
    const [testSending, setTestSending] = useState(false);
    // Templates
    const [templates, setTemplates] = useState([]);
    const [showTemplates, setShowTemplates] = useState(false);
    const [showEmailPreview, setShowEmailPreview] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoaded(!editId);
        setLoadError('');
        api.getWorkspaces().then(setWorkspaces).catch(() => {});
        api.getLists().then(setLists).catch(() => {});
        api.getTemplates().then(setTemplates).catch(() => {});
        api.getScheduleProfiles().then(p=>{const d=p.find(x=>x.is_default);if(d)setScheduleProfileId(v=>v||d.id);}).catch(()=>{});
        const accountRequest = api.getCurrentAccount().catch(() => null);
        let loadedCampaign = null;
        let accountTimezone = '';
        const loadScheduleAt = (value, tz) => {
            if (!value) { setScheduleAt(''); return; }
            try {
                const utcDate = new Date(value);
                if (tz) {
                    // Convert UTC to account timezone for datetime-local input
                    const local = new Date(utcDate.toLocaleString('en-US', { timeZone: tz }));
                    const y = local.getFullYear();
                    const mo = String(local.getMonth() + 1).padStart(2, '0');
                    const d = String(local.getDate()).padStart(2, '0');
                    const h = String(local.getHours()).padStart(2, '0');
                    const mi = String(local.getMinutes()).padStart(2, '0');
                    setScheduleAt(`${y}-${mo}-${d}T${h}:${mi}`);
                } else {
                    setScheduleAt(utcDate.toISOString().slice(0, 16));
                }
            } catch { setScheduleAt(''); }
        };

        accountRequest.then(account => {
            if (cancelled) return;
            accountTimezone = account?.timezone || '';
            setTimezone(accountTimezone);
            if (loadedCampaign?.schedule_at) loadScheduleAt(loadedCampaign.schedule_at, accountTimezone);
        });

        if (editId) {
            api.getCampaign(editId).then(c => {
                if (cancelled) return;
                loadedCampaign = c;
                setName(c.name || ''); setSubject(c.subject || ''); setFormat(c.format || 'html');
                setFromName(c.from_name || '');
                setRawHtml(c.html_body || c.body || ''); setHtmlBody(c.html_body || c.body || '');
                setTextBody(c.text_body || ''); setSelectedList(c.list_id || '');
                setRotation(c.rotation_strategy || 'round-robin'); setPace(c.sending_pace || 'normal');
                if (c.workspace_ids) setSelectedWS(c.workspace_ids);
                if (c.sender_domain_ids) setSelectedDomains(c.sender_domain_ids);
                if (c.html_body || c.body) setEditMode('html');
                if (c.max_per_hour) setMaxPerHour(String(c.max_per_hour));
                if (c.schedule_profile_id) setScheduleProfileId(c.schedule_profile_id);
                if(c.campaign_schedule){const cs=typeof c.campaign_schedule==='string'?JSON.parse(c.campaign_schedule):c.campaign_schedule;
                    setSchedulePreset('custom');setCampaignDailyLimit(cs.daily_limit||12);setScheduleTimezone(cs.timezone||'America/New_York');
                    setWeekdayWindows(cs.weekday_windows||[{start:'09:00',end:'17:00'}]);setIncludeWeekdays(cs.include_weekdays!==false);setIncludeWeekends(!!cs.include_weekends);
                    setWeekendUseWeekdayHours(cs.weekend_use_weekday_hours!==false);
                    setWeekendWindows(cs.weekend_windows||[{start:'10:00',end:'16:00'}]);setMinimumSpacing(cs.minimum_spacing_minutes||20);}
                // A/B fields
                if (c.ab_enabled) { setAbEnabled(true); setAbSubjectB(c.ab_variant_b_subject || ''); setAbHtmlBodyB(c.ab_variant_b_html_body || ''); setAbTextBodyB(c.ab_variant_b_text_body || ''); }
                if (c.ab_split_percent) setAbSplitPct(c.ab_split_percent);
                if (c.ab_winner_metric) setAbWinnerMetric(c.ab_winner_metric);
                loadScheduleAt(c.schedule_at, accountTimezone);
                setLoaded(true);
            }).catch(err => {
                if (cancelled) return;
                setLoadError(err.message || 'Failed to load campaign');
                setLoaded(true);
            });
        }

        return () => { cancelled = true; };
    }, [editId]);

    // Load contact count when reaching step 3
    useEffect(() => {
        if (step === 3) {
            const params = selectedList ? { list_id: selectedList, limit: 1 } : { limit: 1 };
            api.getContacts(params).then(d => setContactCount(d.total)).catch(() => {});
        }
    }, [step, selectedList]);

    const applySchedulePreset=(preset)=>{
        const presets={workday:[{start:'09:00',end:'17:00'}],after_hours:[{start:'17:00',end:'21:00'}],midday:[{start:'12:00',end:'16:00'}]};
        setSchedulePreset(preset);
        if(presets[preset]) setWeekdayWindows(presets[preset].map(w=>({...w})));
    };
    const updateWindow=(weekend,index,key,value)=>{const setter=weekend?setWeekendWindows:setWeekdayWindows;setter(rows=>rows.map((r,i)=>i===index?{...r,[key]:value}:r));setSchedulePreset('custom');};
    const addWindow=(weekend)=>{(weekend?setWeekendWindows:setWeekdayWindows)(rows=>[...rows,{start:'09:00',end:'17:00'}]);setSchedulePreset('custom');};
    const removeWindow=(weekend,index)=>{(weekend?setWeekendWindows:setWeekdayWindows)(rows=>rows.filter((_,i)=>i!==index));setSchedulePreset('custom');};
    const currentHtml = editMode === 'html' ? rawHtml : htmlBody;

    const importHtml = (html, fileName) => {
        setRawHtml(html);
        setHtmlBody(html);
        setEditMode('html');
        setToast({ message: `${fileName} imported`, type: 'success' });
    };

    const save = async (launch = false) => {
        setSaving(true);
        try {
            const body = format === 'text' ? textBody : currentHtml;
            const payload = {
                name, subject, body, html_body: format !== 'text' ? body : '', text_body: textBody,
                from_name: fromName || null,
                format, rotation_strategy: rotation, sending_pace: pace,
                list_id: selectedList || null, workspace_ids: selectedWS,
                sender_domain_ids: selectedDomains,
                schedule_at: scheduleAt || null,
                max_per_hour: maxPerHour ? parseInt(maxPerHour) : null,
                ab_enabled: abEnabled,
                ab_variant_b_subject: abEnabled ? abSubjectB : null,
                ab_variant_b_html_body: abEnabled ? abHtmlBodyB : null,
                ab_variant_b_text_body: abEnabled ? abTextBodyB : null,
                ab_split_percent: abSplitPct,
                ab_winner_metric: abWinnerMetric,
                delivery_mode: 'gmail_operations',
                schedule_profile_id: scheduleProfileId || null,
                campaign_schedule:{daily_limit:Math.max(1,parseInt(campaignDailyLimit)||12),timezone:scheduleTimezone,
                    weekday_windows:weekdayWindows,include_weekdays:includeWeekdays,include_weekends:includeWeekends,
                    weekend_use_weekday_hours:weekendUseWeekdayHours,weekend_windows:weekendWindows,
                    minimum_spacing_minutes:Math.max(1,parseInt(minimumSpacing)||20)},
            };
            let campaign;
            if (editId) {
                campaign = await api.updateCampaign(editId, payload);
                campaign = { ...campaign, id: editId };
            } else {
                campaign = await api.createCampaign(payload);
            }
            if (launch) {
                const preflight = await api.preflightCampaign(campaign.id);
                if (!preflight.ready) {
                    setToast({ message: `Can't launch: ${preflight.issues.join(', ')}`, type: 'error' });
                    setSaving(false); return;
                }
                if (scheduleAt && new Date(scheduleAt) > new Date()) {
                    // Save as scheduled — the server converts timezone and the scheduler will launch it
                    await api.updateCampaign(campaign.id, {
                        status: 'scheduled',
                        schedule_at: scheduleAt,
                    });
                    setToast({ message: `Campaign scheduled!`, type: 'success' });
                } else {
                    await api.launchCampaign(campaign.id);
                    setToast({ message: 'Campaign launched!', type: 'success' });
                }
            } else {
                setToast({ message: editId ? 'Campaign updated' : 'Campaign saved as draft', type: 'info' });
            }
            setTimeout(() => navigate('/campaigns'), 1000);
        } catch (err) { setToast({ message: err.message, type: 'error' }); }
        setSaving(false);
    };

    if (!loaded) {
        return <div style={{ padding: 32, color: C.muted }}>Loading campaign…</div>;
    }

    if (loadError) {
        return (
            <div style={{ background: C.card, border: `1px solid ${C.danger}`, borderRadius: 12, padding: 24 }}>
                <div style={{ color: C.danger, fontWeight: 600, marginBottom: 8 }}>Could not open campaign editor</div>
                <div style={{ color: C.muted, fontSize: 13, marginBottom: 18 }}>{loadError}</div>
                <button type="button" onClick={() => navigate('/campaigns')} style={btnSecondary}>← Back to campaigns</button>
            </div>
        );
    }

    return (
        <div className="campaign-editor" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)' }}>
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
            {showEmailPreview && (
                <EmailPreviewModal subject={subject} html={format === 'text' ? '' : currentHtml} text={textBody} onClose={() => setShowEmailPreview(false)} />
            )}
            <Tabs tabs={[{ key: 1, label: '1. Details' }, { key: 2, label: '2. Compose Email' }, { key: 3, label: '3. Workspaces & Send' }]}
                active={step} onChange={setStep} />

            {/* Step 1: Details */}
            {step === 1 && (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 600 }}>
                        <div><label style={labelStyle}>Campaign Name</label><input style={inputStyle} placeholder="e.g. Q1 Retailer Outreach" value={name} onChange={e => setName(e.target.value)} /></div>
                        <div><label style={labelStyle}>Subject Line</label><input style={inputStyle} placeholder="e.g. {{first_name}}, let's talk distribution" value={subject} onChange={e => setSubject(e.target.value)} />
                            <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>Merge fields: {'{{first_name}} {{last_name}} {{company}} {{title}} {{email}} {{unsubscribe}} {{unsubscribe_url}}'}</div></div>
                        <div>
                            <label style={labelStyle}>From Name</label>
                            <input style={inputStyle} placeholder="Optional, defaults to workspace name" value={fromName} onChange={e => setFromName(e.target.value)} />
                        </div>
                        <div><label style={labelStyle}>Contact List</label>
                            <select style={inputStyle} value={selectedList} onChange={e => setSelectedList(e.target.value)}>
                                <option value="">All contacts</option>
                                {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.contact_count} contacts)</option>)}
                            </select>
                        </div>
                        <div><label style={labelStyle}>Email Format</label>
                            <select style={inputStyle} value={format} onChange={e => setFormat(e.target.value)}>
                                <option value="html">HTML Email</option>
                                <option value="text">Plain Text Only</option>
                                <option value="both">HTML + Text Version</option>
                            </select>
                        </div>
                        {/* Template Load */}
                        {templates.length > 0 && (
                            <div><label style={labelStyle}>Load from Template</label>
                                <select style={inputStyle} value="" onChange={e => {
                                    const t = templates.find(t => t.id === e.target.value);
                                    if (t) {
                                        if (t.subject) setSubject(t.subject);
                                        if (t.html_body) { setRawHtml(t.html_body); setHtmlBody(t.html_body); setEditMode('html'); }
                                        if (t.text_body) setTextBody(t.text_body);
                                        if (t.format) setFormat(t.format);
                                        setToast({ message: `Template "${t.name}" loaded`, type: 'success' });
                                    }
                                }}>
                                    <option value="">Select a template...</option>
                                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                            </div>
                        )}
                    </div>
                    <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
                        <button onClick={() => save(false)} disabled={saving || !name} style={{ ...btnSecondary, opacity: name && !saving ? 1 : 0.5 }}>💾 Save Draft</button>
                        <button onClick={() => setStep(2)} style={btnPrimary}>Next: Compose Email →</button>
                    </div>
                </div>
            )}

            {/* Step 2: Email Composer */}
            {step === 2 && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${C.border}`, alignItems: 'center', minHeight: 46 }}>
                        {format !== 'text' && (
                            <>
                            {[{ key: 'designer', label: 'Visual Designer' }, { key: 'html', label: 'HTML Code' }].map(m => (
                                <button key={m.key} onClick={() => {
                                    if (m.key === 'html' && editMode === 'designer') setRawHtml(htmlBody);
                                    if (m.key === 'designer' && editMode === 'html') setHtmlBody(rawHtml);
                                    setEditMode(m.key);
                                }} style={{ padding: '10px 20px', background: editMode === m.key ? C.active : 'transparent',
                                    border: 'none', borderBottom: editMode === m.key ? `2px solid ${C.accent}` : '2px solid transparent',
                                    color: editMode === m.key ? C.text : C.muted, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
                                    {m.label}
                                </button>
                            ))}
                            </>
                        )}
                        <div style={{ flex: 1 }} />
                        {format !== 'text' && (
                            <HtmlImportButton onImport={importHtml} onError={message => setToast({ message, type: 'error' })} />
                        )}
                        <button type="button" onClick={() => setShowEmailPreview(true)}
                            style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11, marginLeft: 8, whiteSpace: 'nowrap' }}>
                            ◎ Preview
                        </button>
                        <button type="button" onClick={() => { if (window.confirm('Clear all email content? This cannot be undone.')) { setHtmlBody(''); setRawHtml(''); setBlocks([createBlock('text')]); setTextBody(''); } }}
                            style={{ padding: '6px 12px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 6, color: C.muted, cursor: 'pointer', fontSize: 11, margin: '0 8px', whiteSpace: 'nowrap' }}>
                            🗑 Clear
                        </button>
                    </div>

                    <div style={{ flex: 1, overflow: 'auto' }}>
                        {format === 'text' ? (
                            <textarea style={{ ...inputStyle, height: '100%', minHeight: 400, resize: 'none', border: 'none', borderRadius: 0, fontFamily: 'monospace', fontSize: 14, lineHeight: 1.6 }}
                                placeholder="Write your plain text email here...&#10;&#10;Hi {{first_name}},&#10;&#10;I wanted to reach out about..."
                                value={textBody} onChange={e => setTextBody(e.target.value)} />
                        ) : editMode === 'designer' ? (
                            <EmailDesigner blocks={blocks} onChange={setBlocks} onHtmlChange={setHtmlBody} />
                        ) : (
                            <textarea style={{ ...inputStyle, height: '100%', minHeight: 400, resize: 'none', border: 'none', borderRadius: 0, fontFamily: 'monospace', fontSize: 13, lineHeight: 1.5 }}
                                placeholder="Paste or write your HTML email code here..."
                                value={rawHtml} onChange={e => setRawHtml(e.target.value)} />
                        )}
                    </div>

                    {format === 'both' && (
                        <div style={{ borderTop: `1px solid ${C.border}`, maxHeight: 200 }}>
                            <div style={{ padding: '8px 16px', fontSize: 12, color: C.muted, fontWeight: 600, background: C.hover }}>Plain Text Version</div>
                            <textarea style={{ ...inputStyle, border: 'none', borderRadius: 0, fontFamily: 'monospace', fontSize: 13, height: 150, resize: 'none' }}
                                placeholder="Plain text fallback..." value={textBody} onChange={e => setTextBody(e.target.value)} />
                        </div>
                    )}

                    <div style={{ padding: '12px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {/* A/B Testing Toggle */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                                <input type="checkbox" checked={abEnabled} onChange={e => setAbEnabled(e.target.checked)} />
                                <span style={{ fontWeight: 600 }}>🧪 A/B Split Test</span>
                            </label>
                            {abEnabled && (
                                <div style={{ display: 'flex', gap: 8, fontSize: 12, color: C.muted }}>
                                    <label>Split: <input type="number" min="10" max="90" value={abSplitPct} onChange={e => setAbSplitPct(parseInt(e.target.value) || 50)} style={{ ...inputStyle, width: 50, padding: '2px 6px', display: 'inline' }} />% A</label>
                                    <label>Winner by: <select value={abWinnerMetric} onChange={e => setAbWinnerMetric(e.target.value)} style={{ ...inputStyle, display: 'inline', width: 'auto', padding: '2px 6px' }}>
                                        <option value="opens">Opens</option><option value="clicks">Clicks</option><option value="replies">Replies</option>
                                    </select></label>
                                </div>
                            )}
                        </div>
                        {abEnabled && (
                            <div style={{ background: C.hover, borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: C.accent }}>Variant B</div>
                                <input style={inputStyle} placeholder="Variant B subject line (leave blank to use same subject)" value={abSubjectB} onChange={e => setAbSubjectB(e.target.value)} />
                                {format === 'text' ? (
                                    <textarea style={{ ...inputStyle, height: 100, fontFamily: 'monospace', fontSize: 13 }} placeholder="Variant B text body (leave blank to use same body)" value={abTextBodyB} onChange={e => setAbTextBodyB(e.target.value)} />
                                ) : (
                                    <textarea style={{ ...inputStyle, height: 100, fontFamily: 'monospace', fontSize: 13 }} placeholder="Variant B HTML (leave blank to use same body)" value={abHtmlBodyB} onChange={e => setAbHtmlBodyB(e.target.value)} />
                                )}
                            </div>
                        )}
                        {/* Test Send */}
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input style={{ ...inputStyle, flex: 1, margin: 0 }} placeholder="Send test to email..." value={testEmail} onChange={e => setTestEmail(e.target.value)} />
                            <button disabled={!testEmail || testSending || !name} onClick={async () => {
                                setTestSending(true);
                                try {
                                    // Auto-save before testing
                                    if (editMode === 'html') setHtmlBody(rawHtml);
                                    const body = editMode === 'html' ? rawHtml : htmlBody;
                                    const payload = {
                                        name, subject, body, html_body: format !== 'text' ? body : '', text_body: textBody,
                                        from_name: fromName || null,
                                        format, rotation_strategy: rotation, sending_pace: pace,
                                        list_id: selectedList || null, workspace_ids: selectedWS, sender_domain_ids: selectedDomains,
                                        schedule_at: scheduleAt || null,
                                        max_per_hour: maxPerHour ? parseInt(maxPerHour) : null,
                                        ab_enabled: abEnabled,
                                        ab_variant_b_subject: abEnabled ? abSubjectB : null,
                                        ab_variant_b_html_body: abEnabled ? abHtmlBodyB : null,
                                        ab_variant_b_text_body: abEnabled ? abTextBodyB : null,
                                        ab_split_percent: abSplitPct, ab_winner_metric: abWinnerMetric,
                                    };
                                    let cId = editId;
                                    if (editId) {
                                        await api.updateCampaign(editId, payload);
                                    } else {
                                        const created = await api.createCampaign(payload);
                                        cId = created.id;
                                        window.history.replaceState(null, '', `/campaigns/edit/${cId}`);
                                    }
                                    const r = await api.testCampaign(cId, testEmail, 'A');
                                    setToast({ message: r.message, type: 'success' });
                                } catch (e) { setToast({ message: e.message, type: 'error' }); }
                                finally { setTestSending(false); }
                            }} style={{ ...btnSecondary, whiteSpace: 'nowrap', opacity: testEmail && !testSending ? 1 : 0.5 }}>
                                {testSending ? '⏳ Sending...' : '📨 Send Test'}
                            </button>
                            {abEnabled && (
                                <button disabled={!testEmail || testSending || !editId} onClick={async () => {
                                    setTestSending(true);
                                    try {
                                        if (editMode === 'html') setHtmlBody(rawHtml);
                                        await save(false);
                                        const r = await api.testCampaign(editId, testEmail, 'B');
                                        setToast({ message: r.message, type: 'success' });
                                    } catch (e) { setToast({ message: e.message, type: 'error' }); }
                                    finally { setTestSending(false); }
                                }} style={{ ...btnSecondary, whiteSpace: 'nowrap', opacity: testEmail && !testSending ? 1 : 0.5 }}>
                                    📨 Test B
                                </button>
                            )}
                        </div>
                        <div style={{ fontSize: 10, color: C.dim, marginTop: -6 }}>
                            Test emails include the automatic unsubscribe footer and tracking pixel, but are excluded from campaign and inbox open-rate analytics.
                        </div>
                        {/* Nav buttons */}
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <button onClick={() => setStep(1)} style={btnSecondary}>← Back</button>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button onClick={() => { if (editMode === 'html') setHtmlBody(rawHtml); save(false); }} disabled={saving} style={btnSecondary}>💾 Save Draft</button>
                                <button onClick={() => { if (editMode === 'html') setHtmlBody(rawHtml); setStep(3); }} style={btnPrimary}>Next: Configure Send →</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Step 3: Workspaces & Send */}
            {step === 3 && (() => {
                const paceRates = { conservative: [60, 120], normal: [120, 240], aggressive: [240, 720] };
                const rates = paceRates[pace] || paceRates.normal;
                const wsCount = selectedWS.length || 1;
                const effectiveRate = maxPerHour ? Math.min(parseInt(maxPerHour), rates[1] * wsCount) : rates[1] * wsCount;
                const estMinutes = contactCount ? Math.ceil(contactCount / (effectiveRate / 60)) : null;
                return (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, overflowY: 'auto', flex: 1 }}>
                    {/* Pre-launch Summary */}
                    <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                        {[{ label: 'Recipients', value: contactCount ?? '...', icon: '👥' },
                          { label: 'Sender selection', value: `${selectedDomains.length}D / ${selectedWS.length}I`, icon: '📤' },
                          { label: 'Est. Rate', value: `~${effectiveRate}/hr`, icon: '⚡' },
                          { label: 'Est. Time', value: estMinutes ? (estMinutes < 60 ? `~${estMinutes}m` : `~${Math.ceil(estMinutes/60)}h ${estMinutes%60}m`) : '—', icon: '⏱' },
                        ].map(s => (
                            <div key={s.label} style={{ flex: '1 1 120px', padding: '14px 16px', background: C.hover, borderRadius: 10, textAlign: 'center' }}>
                                <div style={{ fontSize: 22 }}>{s.icon}</div>
                                <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'monospace', marginTop: 2 }}>{s.value}</div>
                                <div style={{ fontSize: 11, color: C.muted }}>{s.label}</div>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div>
                            <label style={{ ...labelStyle, marginBottom: 10 }}>Select sender domains or individual inboxes</label>
                            {workspaces.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: C.dim }}>No workspaces. <Link to="/workspaces" style={{ color: C.accent }}>Connect one first →</Link></div>}
                            {Object.entries(workspaces.reduce((groups,ws)=>{const key=ws.sender_domain_id||ws.domain||'ungrouped';if(!groups[key])groups[key]={id:ws.sender_domain_id,name:ws.domain||'Ungrouped',inboxes:[]};groups[key].inboxes.push(ws);return groups;},{})).map(([key,group])=>{
                                const domainSelected=group.id&&selectedDomains.includes(group.id);
                                const toggleDomain=()=>{if(!group.id)return;setSelectedDomains(p=>p.includes(group.id)?p.filter(id=>id!==group.id):[...p,group.id]);if(!domainSelected)setSelectedWS(p=>p.filter(id=>!group.inboxes.some(w=>w.id===id)));};
                                return <div key={key} style={{border:`1px solid ${domainSelected?C.accent:C.border}`,borderRadius:10,marginBottom:10,overflow:'hidden'}}>
                                    <div onClick={toggleDomain} style={{display:'flex',alignItems:'center',gap:10,padding:'12px 14px',background:domainSelected?C.accentDim:C.hover,cursor:group.id?'pointer':'default'}}>
                                        <div style={{width:20,height:20,borderRadius:4,border:`2px solid ${domainSelected?C.accent:C.borderLight}`,background:domainSelected?C.accent:'transparent',textAlign:'center'}}>{domainSelected&&'✓'}</div>
                                        <div style={{flex:1}}><b>{group.name}</b><div style={{fontSize:11,color:C.muted}}>{group.inboxes.length} inbox{group.inboxes.length===1?'':'es'} · selecting includes current and future inboxes</div></div>
                                        <span style={{fontSize:11,color:C.accent}}>{domainSelected?'Entire domain selected':'Select domain'}</span>
                                    </div>
                                    <div style={{padding:'6px 12px 10px 42px'}}>{group.inboxes.map(ws=>{
                                        const selected=domainSelected||selectedWS.includes(ws.id);return <div key={ws.id} onClick={()=>{if(domainSelected)return;setSelectedWS(p=>p.includes(ws.id)?p.filter(id=>id!==ws.id):[...p,ws.id]);}}
                                          style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',opacity:domainSelected ? .65 : 1,cursor:domainSelected?'not-allowed':'pointer',borderTop:`1px solid ${C.border}`}}>
                                            <div style={{width:16,height:16,borderRadius:3,border:`1px solid ${selected?C.accent:C.borderLight}`,background:selected?C.accent:'transparent',textAlign:'center',fontSize:11}}>{selected&&'✓'}</div>
                                            <span style={{flex:1,fontSize:12}}>{ws.display_name||ws.email}<br/><small style={{color:C.muted}}>{ws.email}</small></span><span style={{fontFamily:'monospace',fontSize:11}}>{ws.daily_sent_count}/{ws.per_day_cap||12}</span>
                                        </div>;})}</div>
                                </div>;
                            })}
                        </div>
                        <div style={{background:C.hover,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}>
                            <label style={{...labelStyle,marginBottom:10}}>Campaign sending schedule</label>
                            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:16}}>{[
                                ['workday','Workday','09:00–17:00'],['after_hours','After hours','17:00–21:00'],['midday','Midday','12:00–16:00'],['custom','Custom','Your own windows']
                            ].map(([id,title,hours])=><button type="button" key={id} onClick={()=>applySchedulePreset(id)} style={{flex:'1 1 135px',padding:'10px 12px',borderRadius:8,cursor:'pointer',textAlign:'left',color:C.text,background:schedulePreset===id?C.accentDim:C.card,border:`1px solid ${schedulePreset===id?C.accent:C.border}`}}><b>{title}</b><div style={{fontSize:10,color:C.muted,marginTop:2}}>{hours}</div></button>)}</div>
                            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:14}}>
                                <div><label style={labelStyle}>Daily limit per inbox</label><input style={inputStyle} type="number" min="1" max="100" value={campaignDailyLimit} onChange={e=>setCampaignDailyLimit(e.target.value)}/><div style={{fontSize:10,color:C.dim,marginTop:3}}>Cannot exceed the inbox safety cap.</div></div>
                                <div><label style={labelStyle}>Timezone</label><select style={inputStyle} value={scheduleTimezone} onChange={e=>{setScheduleTimezone(e.target.value);setSchedulePreset('custom');}}><option value="America/New_York">US Eastern</option><option value="America/Chicago">US Central</option><option value="America/Denver">US Mountain</option><option value="America/Los_Angeles">US Pacific</option><option value="America/Phoenix">US Arizona</option><option value="Pacific/Honolulu">US Hawaii</option></select></div>
                                <div><label style={labelStyle}>Minimum spacing</label><div style={{display:'flex',alignItems:'center',gap:8}}><input style={inputStyle} type="number" min="1" value={minimumSpacing} onChange={e=>{setMinimumSpacing(e.target.value);setSchedulePreset('custom');}}/><span style={{fontSize:11,color:C.muted}}>minutes</span></div></div>
                            </div>
                            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12}}>
                                <div onClick={()=>{setIncludeWeekdays(v=>!v);setSchedulePreset('custom');}} style={{padding:12,borderRadius:9,cursor:'pointer',background:includeWeekdays?C.accentDim:C.card,border:`1px solid ${includeWeekdays?C.accent:C.border}`}}><b>{includeWeekdays?'✓ ':''}Weekdays</b><div style={{fontSize:10,color:C.muted}}>Monday through Friday</div></div>
                                <div onClick={()=>{setIncludeWeekends(v=>!v);setSchedulePreset('custom');}} style={{padding:12,borderRadius:9,cursor:'pointer',background:includeWeekends?C.accentDim:C.card,border:`1px solid ${includeWeekends?C.accent:C.border}`}}><b>{includeWeekends?'✓ ':''}Weekends</b><div style={{fontSize:10,color:C.muted}}>Saturday and Sunday</div></div>
                            </div>
                            <div style={{display:'flex',gap:6,marginBottom:10}}><button type="button" onClick={()=>setScheduleTab('weekdays')} style={{...btnSecondary,background:scheduleTab==='weekdays'?C.accentDim:C.card,color:scheduleTab==='weekdays'?C.accent:C.muted}}>Weekday hours</button><button type="button" onClick={()=>setScheduleTab('weekends')} style={{...btnSecondary,background:scheduleTab==='weekends'?C.accentDim:C.card,color:scheduleTab==='weekends'?C.accent:C.muted}}>Weekend hours</button></div>
                            {scheduleTab==='weekends'&&includeWeekends&&<label style={{display:'flex',alignItems:'center',gap:8,fontSize:12,marginBottom:10,cursor:'pointer'}}><input type="checkbox" checked={weekendUseWeekdayHours} onChange={e=>{setWeekendUseWeekdayHours(e.target.checked);setSchedulePreset('custom');}}/> Use the same hours as the weekday schedule</label>}
                            {scheduleTab==='weekdays'&&<div>{weekdayWindows.map((w,i)=><div key={i} style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}><input style={{...inputStyle,width:150}} type="time" value={w.start} onChange={e=>updateWindow(false,i,'start',e.target.value)}/><span style={{color:C.muted}}>to</span><input style={{...inputStyle,width:150}} type="time" value={w.end} onChange={e=>updateWindow(false,i,'end',e.target.value)}/><button type="button" onClick={()=>removeWindow(false,i)} style={{background:'none',border:0,color:C.danger,cursor:'pointer'}}>Remove</button></div>)}<button type="button" onClick={()=>addWindow(false)} style={{...btnSecondary,padding:'6px 10px'}}>+ Add weekday window</button></div>}
                            {scheduleTab==='weekends'&&!includeWeekends&&<div style={{padding:12,color:C.muted,fontSize:12}}>Enable Weekends above to send on Saturday or Sunday.</div>}
                            {scheduleTab==='weekends'&&includeWeekends&&weekendUseWeekdayHours&&<div style={{padding:12,color:C.info,fontSize:12,background:C.infoDim,borderRadius:8}}>Weekend sends will use the weekday time windows shown in the other tab.</div>}
                            {scheduleTab==='weekends'&&includeWeekends&&!weekendUseWeekdayHours&&<div>{weekendWindows.map((w,i)=><div key={i} style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}><input style={{...inputStyle,width:150}} type="time" value={w.start} onChange={e=>updateWindow(true,i,'start',e.target.value)}/><span style={{color:C.muted}}>to</span><input style={{...inputStyle,width:150}} type="time" value={w.end} onChange={e=>updateWindow(true,i,'end',e.target.value)}/><button type="button" onClick={()=>removeWindow(true,i)} style={{background:'none',border:0,color:C.danger,cursor:'pointer'}}>Remove</button></div>)}<button type="button" onClick={()=>addWindow(true)} style={{...btnSecondary,padding:'6px 10px'}}>+ Add weekend window</button></div>}
                            <div style={{fontSize:11,color:C.dim,marginTop:12}}>Choose weekdays, weekends, or both. Weekend hours can inherit the weekday schedule or use separate windows.</div>
                        </div>
                        <div style={{ display: 'flex', gap: 16 }}>
                            <div style={{ flex: 1 }}>
                                <label style={labelStyle}>Rotation Strategy</label>
                                <select style={inputStyle} value={rotation} onChange={e => setRotation(e.target.value)}>
                                    <option value="round-robin">Round Robin</option><option value="weighted">Weighted (healthier first)</option><option value="capacity">Capacity (fill lowest first)</option>
                                </select>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 16 }}>
                            <div style={{ flex: 1 }}>
                                <label style={labelStyle}>Max Emails Per Hour (optional)</label>
                                <input style={inputStyle} type="number" placeholder="No limit" value={maxPerHour}
                                    onChange={e => setMaxPerHour(e.target.value)} />
                                <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>Caps total sends across all workspaces. Leave empty for no limit.</div>
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={labelStyle}>Schedule Send (optional)</label>
                                <input style={inputStyle} type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)} />
                                <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>
                                    {timezone ? `Times in ${timezone.replace(/_/g, ' ')}` : 'Using your browser timezone'} · Leave empty to send immediately
                                </div>
                            </div>
                        </div>
                    </div>
                    <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'space-between' }}>
                        <button onClick={() => setStep(2)} style={btnSecondary}>← Back</button>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => save(false)} disabled={saving} style={btnSecondary}>Save Draft</button>
                            <button onClick={() => save(true)} disabled={saving || (!selectedWS.length&&!selectedDomains.length)} style={{ ...btnPrimary, opacity: (selectedWS.length||selectedDomains.length) && !saving ? 1 : 0.5 }}>
                                {saving ? 'Processing...' : scheduleAt && new Date(scheduleAt) > new Date() ? `📅 Schedule Campaign` : '🚀 Launch Campaign'}
                            </button>
                        </div>
                    </div>
                </div>
                );
            })()}
        </div>
    );
}

// ═══════════════════════════════════════════════════════
//  CAMPAIGNS LIST
// ═══════════════════════════════════════════════════════

function CampaignsPage() {
    const navigate = useNavigate();
    const [campaigns, setCampaigns] = useState([]);
    const [toast, setToast] = useState(null);
    const [openingEditorId, setOpeningEditorId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [campaignStats, setCampaignStats] = useState(null);
    const load = useCallback(() => { api.getCampaigns().then(setCampaigns).catch(() => {}); }, []);
    useEffect(load, [load]);

    const toggleStats = async (id) => {
        if (expandedId === id) { setExpandedId(null); setCampaignStats(null); return; }
        setExpandedId(id);
        try { setCampaignStats(await api.getCampaignStats(id)); } catch { setCampaignStats([]); }
    };

    const action = async (id, act) => {
        try {
            if (act === 'launch') { await api.launchCampaign(id); setToast({ message: 'Launched!', type: 'success' }); }
            if (act === 'pause') { await api.pauseCampaign(id); setToast({ message: 'Paused', type: 'warning' }); }
            if (act === 'delete') { if (!window.confirm('Delete?')) return; await api.deleteCampaign(id); }
            if (act === 'duplicate') { await api.duplicateCampaign(id); setToast({ message: 'Duplicated', type: 'info' }); }
            load();
        } catch (err) { setToast({ message: err.message, type: 'error' }); }
    };

    const openEditor = async campaign => {
        if (campaign.status !== 'active') {
            navigate(`/campaigns/edit/${campaign.id}`);
            return;
        }

        setOpeningEditorId(campaign.id);
        try {
            await api.pauseCampaign(campaign.id);
            navigate(`/campaigns/edit/${campaign.id}`);
        } catch (err) {
            setToast({ message: `Could not pause campaign for editing: ${err.message}`, type: 'error' });
        } finally {
            setOpeningEditorId(null);
        }
    };

    return (
        <div>
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
            <div style={{ marginBottom: 16 }}><Link to="/campaigns/new" style={{ ...btnPrimary, textDecoration: 'none' }}>+ New Campaign</Link></div>
            {campaigns.map(c => (
                <div key={c.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 20px', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <StatusBadge status={c.status} />
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 15, fontWeight: 600 }}>{c.name}</div>
                            <div style={{ fontSize: 11, color: C.muted }}>
                                {c.subject || 'No subject'} — {c.format || 'html'} format — {c.list_name || 'All contacts'}
                                {c.status === 'scheduled' && c.schedule_at && <span style={{ color: '#a78bfa' }}> — 📅 Scheduled: {new Date(c.schedule_at).toLocaleString()}</span>}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                            {c.status === 'draft' && <button onClick={() => action(c.id, 'launch')} style={{ ...btnPrimary, padding: '6px 14px', fontSize: 11 }}>Launch</button>}
                            {(c.status === 'draft' || c.status === 'paused' || c.status === 'scheduled') && <button type="button" onClick={() => openEditor(c)} style={{ ...btnSecondary, padding: '6px 14px', fontSize: 11 }}>Edit</button>}
                            {c.status === 'active' && <button type="button" disabled={openingEditorId === c.id} onClick={() => openEditor(c)} style={{ ...btnSecondary, padding: '6px 14px', fontSize: 11, opacity: openingEditorId === c.id ? 0.6 : 1 }}>{openingEditorId === c.id ? 'Pausing…' : 'Edit'}</button>}
                            {c.status === 'scheduled' && <button onClick={() => action(c.id, 'launch')} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: C.successDim, color: C.success, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit' }}>Launch Now</button>}
                            {c.status === 'active' && <button onClick={() => action(c.id, 'pause')} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: C.warningDim, color: C.warning, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit' }}>Pause</button>}
                            {c.status === 'paused' && <button onClick={() => action(c.id, 'launch')} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: C.successDim, color: C.success, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit' }}>Resume</button>}
                            <button onClick={() => action(c.id, 'duplicate')} style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11 }}>Copy</button>
                            <button onClick={async () => { try { await api.saveAsTemplate(c.id, `Template: ${c.name}`); setToast({ message: 'Saved as template!', type: 'success' }); } catch(e) { setToast({ message: `Template save failed: ${e.message}`, type: 'error' }); } }} style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11 }}>📋 Template</button>
                            {(c.status === 'completed' || c.status === 'paused') && parseInt(c.total_sent) > 0 && (
                                <button onClick={() => api.exportCampaign(c.id, c.name)} style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11 }}>📥 Export</button>
                            )}
                            {(c.status === 'completed' || c.status === 'paused') && (
                                <button onClick={async () => { try { const r = await api.retryCampaign(c.id); setToast?.({ message: `Retrying ${r.retried} failed sends`, type: 'success' }); load(); } catch(e) { alert(e.message); } }} style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11 }}>🔄 Retry</button>
                            )}
                            <button onClick={() => action(c.id, 'delete')} style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11, color: C.danger }}>Delete</button>
                        </div>
                    </div>
                    {parseInt(c.total_sent) > 0 && (
                        <div onClick={() => toggleStats(c.id)} style={{ cursor: 'pointer', marginTop: 12 }}>
                            <div style={{ display: 'flex', gap: 24, fontSize: 13, alignItems: 'center' }}>
                                <span><strong style={{ fontFamily: 'monospace' }}>{parseInt(c.total_sent).toLocaleString()}</strong> <span style={{ color: C.muted, fontSize: 11 }}>sent</span></span>
                                <span style={{ position: 'relative', cursor: 'default' }} className="stat-tip"><strong style={{ fontFamily: 'monospace', color: C.info }}><span className="pct">{((c.total_opened / c.total_sent) * 100).toFixed(1)}%</span><span className="cnt">{parseInt(c.total_opened)}</span></strong> <span style={{ color: C.muted, fontSize: 11 }}>opened</span></span>
                                <span style={{ position: 'relative', cursor: 'default' }} className="stat-tip"><strong style={{ fontFamily: 'monospace', color: '#a78bfa' }}><span className="pct">{((c.total_clicked / c.total_sent) * 100).toFixed(1)}%</span><span className="cnt">{parseInt(c.total_clicked)}</span></strong> <span style={{ color: C.muted, fontSize: 11 }}>clicked</span></span>
                                <span style={{ position: 'relative', cursor: 'default' }} className="stat-tip"><strong style={{ fontFamily: 'monospace', color: C.success }}><span className="pct">{((c.total_replied / c.total_sent) * 100).toFixed(1)}%</span><span className="cnt">{parseInt(c.total_replied)}</span></strong> <span style={{ color: C.muted, fontSize: 11 }}>replied</span></span>
                                {parseInt(c.total_bounced) > 0 && <span><strong style={{ fontFamily: 'monospace', color: C.danger }}>{parseInt(c.total_bounced)}</strong> <span style={{ color: C.muted, fontSize: 11 }}>bounced</span></span>}
                                {parseInt(c.total_deferred) > 0 && <span><strong style={{ fontFamily: 'monospace', color: '#f59e0b' }}>{parseInt(c.total_deferred)}</strong> <span style={{ color: C.muted, fontSize: 11 }}>deferred</span></span>}
                                <span style={{ fontSize: 11, color: C.dim, marginLeft: 'auto' }}>{expandedId === c.id ? '▲ Hide workspace stats' : '▼ View by workspace'}</span>
                            </div>
                        </div>
                    )}
                    {expandedId === c.id && campaignStats && (
                        <div style={{ marginTop: 12, background: C.hover, borderRadius: 8, overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                                        {['Workspace', 'Sent', 'Opens', 'Open %', 'Clicks', 'Click %', 'Replies', 'Reply %', 'Bounces'].map(h => (
                                            <th key={h} style={{ padding: '8px 12px', fontSize: 10, color: C.muted, textAlign: 'left', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {campaignStats.map(ws => {
                                        const sent = parseInt(ws.sent) || 0;
                                        const opens = parseInt(ws.opens) || 0;
                                        const clicks = parseInt(ws.clicks) || 0;
                                        const replies = parseInt(ws.replies) || 0;
                                        const bounces = parseInt(ws.bounces) || 0;
                                        const openPct = sent > 0 ? ((opens / sent) * 100).toFixed(1) : '0.0';
                                        const clickPct = sent > 0 ? ((clicks / sent) * 100).toFixed(1) : '0.0';
                                        const replyPct = sent > 0 ? ((replies / sent) * 100).toFixed(1) : '0.0';
                                        return (
                                            <tr key={ws.workspace_id} style={{ borderBottom: `1px solid ${C.border}` }}>
                                                <td style={{ padding: '8px 12px', fontSize: 12 }}>{ws.workspace_email}</td>
                                                <td style={{ padding: '8px 12px', fontSize: 12, fontFamily: 'monospace' }}>{sent}</td>
                                                <td style={{ padding: '8px 12px', fontSize: 12, fontFamily: 'monospace' }}>{opens}</td>
                                                <td style={{ padding: '8px 12px', fontSize: 12, fontFamily: 'monospace', color: parseFloat(openPct) > 30 ? C.success : parseFloat(openPct) > 15 ? C.info : C.danger }}>{openPct}%</td>
                                                <td style={{ padding: '8px 12px', fontSize: 12, fontFamily: 'monospace' }}>{clicks}</td>
                                                <td style={{ padding: '8px 12px', fontSize: 12, fontFamily: 'monospace', color: '#a78bfa' }}>{clickPct}%</td>
                                                <td style={{ padding: '8px 12px', fontSize: 12, fontFamily: 'monospace' }}>{replies}</td>
                                                <td style={{ padding: '8px 12px', fontSize: 12, fontFamily: 'monospace', color: C.success }}>{replyPct}%</td>
                                                <td style={{ padding: '8px 12px', fontSize: 12, fontFamily: 'monospace', color: bounces > 0 ? C.danger : C.dim }}>{bounces}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {campaignStats.length === 0 && <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: C.dim }}>No workspace data yet</div>}
                        </div>
                    )}
                </div>
            ))}
            {campaigns.length === 0 && (
                <div style={{ textAlign: 'center', padding: 60, color: C.dim, background: C.card, borderRadius: 12, border: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>📧</div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>No campaigns yet</div>
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════
//  CONTACTS + LISTS
// ═══════════════════════════════════════════════════════

function ContactsPage() {
    const [tab, setTab] = useState('contacts');
    const [data, setData] = useState({ contacts: [], total: 0 });
    const [lists, setLists] = useState([]);
    const [suppressions, setSuppressions] = useState({ suppressions: [], total: 0 });
    const [search, setSearch] = useState('');
    const [showImport, setShowImport] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [showNewList, setShowNewList] = useState(false);
    const [showEdit, setShowEdit] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [editLists, setEditLists] = useState([]);
    const [importData, setImportData] = useState(null);
    const [importListId, setImportListId] = useState('');
    const [importNewListName, setImportNewListName] = useState('');
    const [addForm, setAddForm] = useState({});
    const [newListForm, setNewListForm] = useState({ name: '', description: '' });
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [toast, setToast] = useState(null);
    const [activeList, setActiveList] = useState(null);
    const [segment, setSegment] = useState('');
    const [segmentDays, setSegmentDays] = useState(30);
    const [bulkListId, setBulkListId] = useState('');
    const [suppressEmail, setSuppressEmail] = useState('');

    const loadContacts = useCallback(() => {
        const params = { search, limit: 100 };
        if (activeList) params.list_id = activeList;
        if (segment) { params.segment = segment; params.segment_days = segmentDays; }
        api.getContacts(params).then(setData).catch(() => {});
    }, [search, activeList, segment, segmentDays]);
    const loadLists = useCallback(() => { api.getLists().then(setLists).catch(() => {}); }, []);
    const loadSuppressions = useCallback(() => { api.getSuppressions({ limit: 200 }).then(setSuppressions).catch(() => {}); }, []);
    useEffect(loadContacts, [loadContacts]);
    useEffect(loadLists, [loadLists]);
    useEffect(() => { if (tab === 'suppressions') loadSuppressions(); }, [tab, loadSuppressions]);

    const handleCSV = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        Papa.parse(file, {
            header: true, skipEmptyLines: true,
            complete: (results) => {
                const contacts = results.data.map(row => ({
                    email: row.email || row.Email || row.EMAIL || '',
                    first_name: row.first_name || row.FirstName || row['First Name'] || '',
                    last_name: row.last_name || row.LastName || row['Last Name'] || '',
                    company: row.company || row.Company || '',
                    title: row.title || row.Title || '',
                })).filter(c => c.email);
                setImportData(contacts);
            },
        });
    };

    const doImport = async () => {
        if (!importData?.length) return;
        try {
            let listId = importListId;
            if (importListId === '__new__' && importNewListName.trim()) {
                const newList = await api.createList(importNewListName.trim(), '');
                listId = newList.id;
            } else if (importListId === '__new__') {
                listId = '';
            }
            const result = await api.importContacts(importData, listId || undefined);
            setToast({ message: `Imported ${result.imported} contacts (${result.skipped} skipped, ${result.suppressed || 0} suppressed)`, type: 'success' });
            setShowImport(false); setImportData(null); setImportListId(''); setImportNewListName(''); loadContacts(); loadLists();
        } catch (err) { setToast({ message: err.message, type: 'error' }); }
    };

    const addContact = async () => {
        try {
            await api.createContact(addForm);
            setToast({ message: 'Contact added', type: 'success' });
            setShowAdd(false); setAddForm({}); loadContacts();
        } catch (err) { setToast({ message: err.message, type: 'error' }); }
    };

    const createList = async () => {
        try {
            await api.createList(newListForm.name, newListForm.description);
            setToast({ message: 'List created', type: 'success' });
            setShowNewList(false); setNewListForm({ name: '', description: '' }); loadLists();
        } catch (err) { setToast({ message: err.message, type: 'error' }); }
    };

    const openEdit = async (contact) => {
        try {
            const full = await api.getContact(contact.id);
            setEditForm({ email: full.email, first_name: full.first_name, last_name: full.last_name,
                company: full.company, title: full.title, status: full.status, tags: full.tags || [] });
            setEditLists(full.lists || []);
            setShowEdit(full);
        } catch (err) { setToast({ message: 'Failed to load contact', type: 'error' }); }
    };

    const saveEdit = async () => {
        try {
            await api.updateContact(showEdit.id, editForm);
            setToast({ message: 'Contact updated', type: 'success' });
            setShowEdit(null); loadContacts();
        } catch (err) { setToast({ message: err.message, type: 'error' }); }
    };

    const toggleList = async (listId) => {
        const isIn = editLists.some(l => l.id === listId);
        try {
            if (isIn) {
                await api.removeFromList(listId, [showEdit.id]);
                setEditLists(editLists.filter(l => l.id !== listId));
            } else {
                await api.addToList(listId, [showEdit.id]);
                const list = lists.find(l => l.id === listId);
                setEditLists([...editLists, { id: listId, name: list?.name }]);
            }
            loadLists();
        } catch (err) { setToast({ message: err.message, type: 'error' }); }
    };

    const bulkAddToList = async () => {
        if (!bulkListId || !selectedContacts.length) return;
        try {
            await api.addToList(bulkListId, selectedContacts);
            setToast({ message: `Added ${selectedContacts.length} contacts to list`, type: 'success' });
            setSelectedContacts([]); setBulkListId(''); loadLists();
        } catch (err) { setToast({ message: err.message, type: 'error' }); }
    };

    const saveSegmentAsList = async () => {
        const segLabels = { opened: 'Opened', clicked: 'Clicked', replied: 'Replied', never_opened: 'Never Opened', bounced: 'Bounced', not_sent: 'Not Sent', unsubscribed: 'Unsubscribed' };
        const name = `${segLabels[segment] || segment} (${segmentDays}d) — ${new Date().toLocaleDateString()}`;
        try {
            const list = await api.createList(name, `Auto-created from segment: ${segment}`);
            const ids = data.contacts.map(c => c.id);
            if (ids.length) await api.addToList(list.id, ids);
            setToast({ message: `Saved ${ids.length} contacts to "${name}"`, type: 'success' }); loadLists();
        } catch (err) { setToast({ message: err.message, type: 'error' }); }
    };

    const toggleSelect = (id) => setSelectedContacts(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
    const toggleAll = () => setSelectedContacts(p => p.length === data.contacts.length ? [] : data.contacts.map(c => c.id));

    return (
        <div>
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}

            <Tabs tabs={[{ key: 'contacts', label: `Contacts (${data.total})` }, { key: 'lists', label: `Lists (${lists.length})` }, { key: 'suppressions', label: `Suppressions (${suppressions.total || 0})` }]}
                active={tab} onChange={setTab} />

            {/* ── Contacts Tab ── */}
            {tab === 'contacts' && (
                <>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                        <input style={{ ...inputStyle, flex: 1, minWidth: 200 }} placeholder="Search contacts..." value={search} onChange={e => setSearch(e.target.value)} />
                        <button onClick={() => setShowAdd(true)} style={btnSecondary}>+ Add</button>
                        <button onClick={() => setShowImport(true)} style={btnPrimary}>📥 Import CSV</button>
                        <button onClick={async () => {
                            setToast({ message: 'Verifying contacts...', type: 'info' });
                            try {
                                const r = await api.verifyContacts({ limit: 1000 });
                                setToast({ message: `Verified ${r.total}: ${r.verified} valid, ${r.invalid} invalid, ${r.risky} risky`, type: 'success' });
                                loadContacts();
                            } catch (e) { setToast({ message: e.message, type: 'error' }); }
                        }} style={btnSecondary}>✅ Verify Emails</button>
                    </div>

                    {/* Segmentation bar */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                        <select style={{ ...inputStyle, width: 'auto', minWidth: 160 }} value={segment} onChange={e => setSegment(e.target.value)}>
                            <option value="">All Contacts</option>
                            <option value="opened">✅ Opened in last...</option>
                            <option value="clicked">🔗 Clicked in last...</option>
                            <option value="replied">💬 Replied in last...</option>
                            <option value="never_opened">🚫 Sent but never opened</option>
                            <option value="bounced">⚠ Bounced</option>
                            <option value="not_sent">📭 Never sent to</option>
                            <option value="unsubscribed">🔕 Unsubscribed</option>
                        </select>
                        {['opened', 'clicked', 'replied'].includes(segment) && (
                            <select style={{ ...inputStyle, width: 'auto' }} value={segmentDays} onChange={e => setSegmentDays(parseInt(e.target.value))}>
                                {[7, 14, 30, 60, 90].map(d => <option key={d} value={d}>{d} days</option>)}
                            </select>
                        )}
                        {activeList && (
                            <button onClick={() => setActiveList(null)} style={{ ...btnSecondary, padding: '8px 12px', fontSize: 11 }}>
                                ✕ {lists.find(l => l.id === activeList)?.name}
                            </button>
                        )}
                        {segment && data.total > 0 && (
                            <button onClick={saveSegmentAsList} style={{ ...btnSecondary, padding: '8px 12px', fontSize: 11, color: C.accent, borderColor: C.accent }}>
                                💾 Save as List ({data.total})
                            </button>
                        )}
                    </div>

                    {/* Bulk actions bar */}
                    {selectedContacts.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12, padding: '10px 14px', background: C.accentDim, borderRadius: 8, alignItems: 'center' }}>
                            <span style={{ fontSize: 12, color: C.accent, fontWeight: 600 }}>{selectedContacts.length} selected</span>
                            <select style={{ ...inputStyle, width: 'auto', minWidth: 140, padding: '6px 10px', fontSize: 12 }} value={bulkListId} onChange={e => setBulkListId(e.target.value)}>
                                <option value="">Add to list...</option>
                                {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                            {bulkListId && <button onClick={bulkAddToList} style={{ ...btnPrimary, padding: '6px 14px', fontSize: 11 }}>Add</button>}
                            <button onClick={async () => { if (window.confirm(`Delete ${selectedContacts.length} contacts?`)) {
                                await api.bulkDeleteContacts(selectedContacts); setSelectedContacts([]); loadContacts(); }}}
                                style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11, color: C.danger, borderColor: C.danger, marginLeft: 'auto' }}>Delete Selected</button>
                        </div>
                    )}

                    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                                    <th style={{ padding: '10px 12px', width: 36 }}>
                                        <input type="checkbox" checked={selectedContacts.length === data.contacts.length && data.contacts.length > 0}
                                            onChange={toggleAll} style={{ cursor: 'pointer' }} />
                                    </th>
                                    {['Name', 'Email', 'Company', 'Status'].map(h => (
                                        <th key={h} style={{ padding: '10px 16px', fontSize: 11, color: C.muted, textAlign: 'left', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
                                    ))}
                                    <th style={{ padding: '10px 16px', width: 60 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.contacts.map(c => (
                                    <tr key={c.id} style={{ borderBottom: `1px solid ${C.border}`, background: selectedContacts.includes(c.id) ? C.accentDim : 'transparent' }}>
                                        <td style={{ padding: '10px 12px' }}>
                                            <input type="checkbox" checked={selectedContacts.includes(c.id)} onChange={() => toggleSelect(c.id)} style={{ cursor: 'pointer' }} />
                                        </td>
                                        <td onClick={() => openEdit(c)} style={{ padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: C.accent }}>
                                            {c.first_name} {c.last_name || '—'}
                                        </td>
                                        <td style={{ padding: '10px 16px', fontSize: 12, color: C.muted, fontFamily: 'monospace' }}>
                                            {c.email}
                                            {c.verified === true && <span title="Email verified" style={{ marginLeft: 4, fontSize: 10 }}>✅</span>}
                                            {c.verified === false && <span title="Email invalid" style={{ marginLeft: 4, fontSize: 10 }}>❌</span>}
                                        </td>
                                        <td style={{ padding: '10px 16px', fontSize: 13 }}>{c.company || '—'}</td>
                                        <td style={{ padding: '10px 16px' }}><StatusBadge status={c.status} /></td>
                                        <td style={{ padding: '10px 16px' }}>
                                            <button onClick={() => openEdit(c)} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 12, marginRight: 6 }}>✎</button>
                                            <button onClick={async () => { if (window.confirm('Delete?')) { await api.deleteContact(c.id); loadContacts(); }}}
                                                style={{ background: 'none', border: 'none', color: C.danger, cursor: 'pointer', fontSize: 12 }}>✕</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {data.contacts.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: C.dim }}>No contacts found</div>}
                        <div style={{ padding: '10px 16px', borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.muted }}>{data.total} total</div>
                    </div>
                </>
            )}

            {/* ── Lists Tab ── */}
            {tab === 'lists' && (
                <>
                    <div style={{ marginBottom: 16 }}>
                        <button onClick={() => setShowNewList(true)} style={btnPrimary}>+ New List</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
                        {lists.map(l => (
                            <div key={l.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                    <div>
                                        <div style={{ fontSize: 15, fontWeight: 600 }}>{l.name}</div>
                                        {l.description && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{l.description}</div>}
                                    </div>
                                    <div style={{ fontSize: 24, fontWeight: 700, color: C.accent, fontFamily: 'monospace' }}>{parseInt(l.contact_count) || 0}</div>
                                </div>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <button onClick={() => { setActiveList(l.id); setTab('contacts'); }} style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11, flex: 1 }}>View Contacts</button>
                                    <button onClick={async () => { await api.copyList(l.id); loadLists(); setToast({ message: 'List copied', type: 'info' }); }}
                                        style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11 }}>Copy</button>
                                    <button onClick={async () => { if (window.confirm(`Delete "${l.name}"?`)) { await api.deleteList(l.id); loadLists(); }}}
                                        style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11, color: C.danger }}>Delete</button>
                                </div>
                            </div>
                        ))}
                    </div>
                    {lists.length === 0 && (
                        <div style={{ textAlign: 'center', padding: 60, color: C.dim, background: C.card, borderRadius: 12, border: `1px solid ${C.border}` }}>
                            <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
                            <div style={{ fontSize: 16, fontWeight: 600 }}>No lists yet</div>
                        </div>
                    )}
                </>
            )}

            {/* ── Suppressions Tab ── */}
            {tab === 'suppressions' && (
                <>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
                        <input style={{ ...inputStyle, flex: 1, maxWidth: 350 }} placeholder="Add email to suppress..." value={suppressEmail}
                            onChange={e => setSuppressEmail(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { addSuppress(); }}} />
                        <button onClick={async () => {
                            if (!suppressEmail.trim()) return;
                            try {
                                await api.addSuppression([suppressEmail.trim()], 'manual');
                                setToast({ message: `${suppressEmail} suppressed`, type: 'success' });
                                setSuppressEmail(''); loadSuppressions();
                            } catch (err) { setToast({ message: err.message, type: 'error' }); }
                        }} style={btnPrimary}>+ Suppress</button>
                    </div>
                    <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>
                        Suppressed emails are automatically skipped during campaign sends. Unsubscribes are added here automatically.
                    </div>
                    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                                    {['Email', 'Reason', 'Source', 'Date', ''].map(h => (
                                        <th key={h} style={{ padding: '10px 16px', fontSize: 11, color: C.muted, textAlign: 'left', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {(suppressions.suppressions || []).map(s => (
                                    <tr key={s.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                                        <td style={{ padding: '10px 16px', fontSize: 12, fontFamily: 'monospace' }}>{s.email}</td>
                                        <td style={{ padding: '10px 16px', fontSize: 12 }}>{s.reason || '—'}</td>
                                        <td style={{ padding: '10px 16px', fontSize: 12, color: C.muted }}>{s.source || '—'}</td>
                                        <td style={{ padding: '10px 16px', fontSize: 11, color: C.dim }}>{s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}</td>
                                        <td style={{ padding: '10px 16px' }}>
                                            <button onClick={async () => {
                                                try {
                                                    await api.removeSuppression(s.id);
                                                    setToast({ message: `${s.email} unsuppressed`, type: 'success' });
                                                    loadSuppressions(); loadContacts();
                                                } catch (err) { setToast({ message: err.message, type: 'error' }); }
                                            }} style={{ background: 'none', border: `1px solid ${C.success}`, color: C.success, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit' }}>Unsuppress</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {(!suppressions.suppressions || suppressions.suppressions.length === 0) && (
                            <div style={{ padding: 40, textAlign: 'center', color: C.dim }}>No suppressed emails</div>
                        )}
                        <div style={{ padding: '10px 16px', borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.muted }}>{suppressions.total || 0} total</div>
                    </div>
                </>
            )}

            {/* Edit Contact Modal */}
            {showEdit && (
                <Modal title="Edit Contact" onClose={() => setShowEdit(null)} width={550}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div><label style={labelStyle}>Email</label>
                            <input style={inputStyle} value={editForm.email || ''} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} /></div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <div style={{ flex: 1 }}><label style={labelStyle}>First Name</label>
                                <input style={inputStyle} value={editForm.first_name || ''} onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))} /></div>
                            <div style={{ flex: 1 }}><label style={labelStyle}>Last Name</label>
                                <input style={inputStyle} value={editForm.last_name || ''} onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))} /></div>
                        </div>
                        <div><label style={labelStyle}>Company</label>
                            <input style={inputStyle} value={editForm.company || ''} onChange={e => setEditForm(f => ({ ...f, company: e.target.value }))} /></div>
                        <div><label style={labelStyle}>Title</label>
                            <input style={inputStyle} value={editForm.title || ''} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} /></div>
                        <div><label style={labelStyle}>Status</label>
                            <select style={inputStyle} value={editForm.status || 'active'} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                                <option value="active">Active</option><option value="unsubscribed">Unsubscribed</option><option value="bounced">Bounced</option>
                            </select></div>

                        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
                            <label style={{ ...labelStyle, marginBottom: 8 }}>List Membership</label>
                            {lists.map(l => {
                                const isIn = editLists.some(el => el.id === l.id);
                                return (
                                    <div key={l.id} onClick={() => toggleList(l.id)}
                                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                                            background: isIn ? C.accentDim : 'transparent', marginBottom: 4 }}>
                                        <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${isIn ? C.accent : C.borderLight}`,
                                            background: isIn ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {isIn && <span style={{ color: '#fff', fontSize: 11 }}>✓</span>}
                                        </div>
                                        <span style={{ fontSize: 13, color: isIn ? C.text : C.muted }}>{l.name}</span>
                                        <span style={{ fontSize: 11, color: C.dim, marginLeft: 'auto' }}>{parseInt(l.contact_count) || 0}</span>
                                    </div>
                                );
                            })}
                            {lists.length === 0 && <div style={{ fontSize: 12, color: C.dim }}>No lists created yet</div>}
                        </div>

                        <button onClick={saveEdit} style={{ ...btnPrimary, marginTop: 4 }}>Save Changes</button>
                    </div>
                </Modal>
            )}

            {/* Add Contact Modal */}
            {showAdd && (
                <Modal title="Add Contact" onClose={() => setShowAdd(false)} width={450}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div><label style={labelStyle}>Email *</label><input style={inputStyle} placeholder="email@example.com" value={addForm.email || ''} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} /></div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <div style={{ flex: 1 }}><label style={labelStyle}>First Name</label><input style={inputStyle} value={addForm.first_name || ''} onChange={e => setAddForm(f => ({ ...f, first_name: e.target.value }))} /></div>
                            <div style={{ flex: 1 }}><label style={labelStyle}>Last Name</label><input style={inputStyle} value={addForm.last_name || ''} onChange={e => setAddForm(f => ({ ...f, last_name: e.target.value }))} /></div>
                        </div>
                        <div><label style={labelStyle}>Company</label><input style={inputStyle} value={addForm.company || ''} onChange={e => setAddForm(f => ({ ...f, company: e.target.value }))} /></div>
                        <div><label style={labelStyle}>Title</label><input style={inputStyle} value={addForm.title || ''} onChange={e => setAddForm(f => ({ ...f, title: e.target.value }))} /></div>
                        {lists.length > 0 && (
                            <div><label style={labelStyle}>Add to List</label>
                                <select style={inputStyle} value={addForm.list_ids?.[0] || ''} onChange={e => setAddForm(f => ({ ...f, list_ids: e.target.value ? [e.target.value] : [] }))}>
                                    <option value="">None</option>
                                    {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                                </select>
                            </div>
                        )}
                        <button onClick={addContact} style={btnPrimary}>Add Contact</button>
                    </div>
                </Modal>
            )}

            {/* Import Modal */}
            {showImport && (
                <Modal title="Import Contacts from CSV" onClose={() => setShowImport(false)}>
                    <div style={{ padding: 24, border: `2px dashed ${C.border}`, borderRadius: 8, textAlign: 'center', marginBottom: 16 }}>
                        <input type="file" accept=".csv" onChange={handleCSV} style={{ display: 'block', margin: '0 auto' }} />
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>CSV columns: email, first_name, last_name, company, title</div>
                    </div>
                    {lists.length >= 0 && (
                        <div style={{ marginBottom: 16 }}><label style={labelStyle}>Import into list</label>
                            <select style={inputStyle} value={importListId} onChange={e => { setImportListId(e.target.value); if (e.target.value !== '__new__') setImportNewListName(''); }}>
                                <option value="">No list (contacts only)</option>
                                <option value="__new__">➕ Create new list...</option>
                                {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                            {importListId === '__new__' && (
                                <input style={{ ...inputStyle, marginTop: 8 }} placeholder="New list name" value={importNewListName} onChange={e => setImportNewListName(e.target.value)} />
                            )}
                        </div>
                    )}
                    {importData && (
                        <div>
                            <div style={{ fontSize: 13, color: C.success, marginBottom: 8 }}>✓ {importData.length} contacts parsed</div>
                            <div style={{ maxHeight: 200, overflow: 'auto', fontSize: 12, background: C.hover, padding: 8, borderRadius: 6 }}>
                                {importData.slice(0, 5).map((c, i) => (
                                    <div key={i} style={{ padding: '4px 0', borderBottom: `1px solid ${C.border}` }}>{c.email} — {c.first_name} {c.last_name}</div>
                                ))}
                                {importData.length > 5 && <div style={{ color: C.muted }}>...and {importData.length - 5} more</div>}
                            </div>
                            <button onClick={doImport} style={{ ...btnPrimary, marginTop: 12, width: '100%' }}>Import {importData.length} Contacts</button>
                        </div>
                    )}
                </Modal>
            )}

            {/* New List Modal */}
            {showNewList && (
                <Modal title="Create New List" onClose={() => setShowNewList(false)} width={420}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div><label style={labelStyle}>List Name *</label><input style={inputStyle} placeholder="e.g. Q1 Prospects" value={newListForm.name} onChange={e => setNewListForm(f => ({ ...f, name: e.target.value }))} /></div>
                        <div><label style={labelStyle}>Description</label><input style={inputStyle} placeholder="Optional description" value={newListForm.description} onChange={e => setNewListForm(f => ({ ...f, description: e.target.value }))} /></div>
                        <button onClick={createList} style={btnPrimary}>Create List</button>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════
//  ACCOUNT SETTINGS — CAN-SPAM + Company Info
// ═══════════════════════════════════════════════════════

function AccountPage() {
    const [account, setAccount] = useState(null);
    const [members, setMembers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [showInvite, setShowInvite] = useState(false);
    const [showAddUser, setShowAddUser] = useState(false);
    const [inviteForm, setInviteForm] = useState({ email: '', role: 'viewer' });
    const [addUserForm, setAddUserForm] = useState({ email: '', password: '', name: '', role: 'viewer' });
    const [form, setForm] = useState({});
    const [toast, setToast] = useState(null);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState('company');

    useEffect(() => {
        api.getCurrentAccount().then(a => { setAccount(a); setForm(a); }).catch(() => {});
        api.getMembers().then(setMembers).catch(() => {});
        api.getInvitations().then(setInvitations).catch(() => []);
    }, []);

    const saveAccount = async () => {
        setSaving(true);
        try {
            await api.updateAccount(form);
            setToast({ message: 'Account updated', type: 'success' });
        } catch (err) { setToast({ message: err.message, type: 'error' }); }
        setSaving(false);
    };

    const invite = async () => {
        try {
            const result = await api.inviteUser(inviteForm.email, inviteForm.role);
            setToast({ message: `Invitation created! Share this link: ${result.invite_url}`, type: 'success' });
            setShowInvite(false); api.getInvitations().then(setInvitations);
        } catch (err) { setToast({ message: err.message, type: 'error' }); }
    };

    const directAdd = async () => {
        try {
            await api.addUser(addUserForm.email, addUserForm.password, addUserForm.name, addUserForm.role);
            setToast({ message: `User added as ${addUserForm.role}`, type: 'success' });
            setShowAddUser(false); api.getMembers().then(setMembers);
        } catch (err) { setToast({ message: err.message, type: 'error' }); }
    };

    if (!account) return <div style={{ color: C.muted, padding: 40 }}>Loading...</div>;

    const F = (key, label, placeholder) => (
        <div><label style={labelStyle}>{label}</label>
            <input style={inputStyle} placeholder={placeholder} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
        </div>
    );

    return (
        <div style={{ maxWidth: 800 }}>
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}

            <Tabs tabs={[{ key: 'company', label: 'Company Info (CAN-SPAM)' }, { key: 'team', label: `Team (${members.length})` }]}
                active={tab} onChange={setTab} />

            {/* Company Info */}
            {tab === 'company' && (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
                    <div style={{ padding: 12, background: C.warningDim, borderRadius: 8, marginBottom: 20, fontSize: 13, color: C.warning }}>
                        ⚠ CAN-SPAM Compliance: Company name and address are <strong>required</strong> before you can launch campaigns. This info is automatically added to every email footer.
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {F('name', 'Account Name', 'e.g. SERVD Distribution')}
                        {F('company_name', 'Company Name *', 'SERVD Distribution LLC')}
                        {F('company_address', 'Street Address *', '123 Main St')}
                        <div style={{ display: 'flex', gap: 12 }}>
                            <div style={{ flex: 2 }}>{F('company_city', 'City', 'Milwaukee')}</div>
                            <div style={{ flex: 1 }}>{F('company_state', 'State', 'WI')}</div>
                            <div style={{ flex: 1 }}>{F('company_zip', 'ZIP', '53202')}</div>
                        </div>
                        {F('company_country', 'Country', 'US')}
                        {F('company_website', 'Website', 'https://servd.com')}
                        {F('company_phone', 'Phone', '(555) 123-4567')}
                        <div>
                            <label style={labelStyle}>Timezone</label>
                            <select style={inputStyle} value={form.timezone || ''} onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))}>
                                <option value="">Auto-detect</option>
                                {['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
                                  'America/Anchorage', 'Pacific/Honolulu', 'America/Phoenix',
                                  'Europe/London', 'Europe/Lisbon', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome',
                                  'Europe/Amsterdam', 'Europe/Madrid', 'Europe/Zurich', 'Europe/Warsaw',
                                  'Europe/Athens', 'Europe/Helsinki', 'Europe/Bucharest', 'Europe/Moscow',
                                  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Bangkok', 'Asia/Shanghai',
                                  'Asia/Tokyo', 'Asia/Seoul', 'Australia/Sydney', 'Australia/Melbourne',
                                  'Pacific/Auckland', 'America/Sao_Paulo', 'America/Mexico_City',
                                  'America/Toronto', 'America/Vancouver', 'Africa/Johannesburg',
                                  'Africa/Lagos', 'Africa/Cairo',
                                ].map(tz => <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>)}
                            </select>
                            <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>Used for scheduled campaign send times</div>
                        </div>
                    </div>
                    <div style={{ marginTop: 20 }}>
                        <button onClick={saveAccount} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving...' : 'Save Account Settings'}</button>
                    </div>
                </div>
            )}

            {/* Team */}
            {tab === 'team' && (
                <div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                        <button onClick={() => setShowAddUser(true)} style={btnPrimary}>+ Add User</button>
                        <button onClick={() => setShowInvite(true)} style={btnSecondary}>📧 Invite by Email</button>
                    </div>

                    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                        {members.map(m => (
                            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderBottom: `1px solid ${C.border}` }}>
                                <div style={{ width: 36, height: 36, borderRadius: '50%', background: C.accentDim, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: C.accent, fontSize: 14 }}>
                                    {(m.name || m.email || '?')[0].toUpperCase()}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 14, fontWeight: 600 }}>{m.name || 'Unnamed'}</div>
                                    <div style={{ fontSize: 12, color: C.muted }}>{m.email}</div>
                                </div>
                                <StatusBadge status={m.role} />
                                {m.role !== 'owner' && (
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        <select style={{ ...inputStyle, width: 100, padding: '4px 8px', fontSize: 11 }}
                                            value={m.role} onChange={async (e) => {
                                                try { await api.changeMemberRole(m.user_id, e.target.value); api.getMembers().then(setMembers); }
                                                catch (err) { setToast({ message: err.message, type: 'error' }); }
                                            }}>
                                            <option value="admin">Admin</option><option value="viewer">Viewer</option>
                                        </select>
                                        <button onClick={async () => {
                                            if (window.confirm(`Remove ${m.email}?`)) {
                                                await api.removeMember(m.user_id); api.getMembers().then(setMembers);
                                            }
                                        }} style={{ background: 'none', border: 'none', color: C.danger, cursor: 'pointer', fontSize: 14 }}>✕</button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {invitations.length > 0 && (
                        <div style={{ marginTop: 16 }}>
                            <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Pending Invitations</h4>
                            {invitations.map(inv => (
                                <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 6 }}>
                                    <div style={{ flex: 1 }}>
                                        <span style={{ fontSize: 13, fontWeight: 500 }}>{inv.email}</span>
                                        <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>as {inv.role}</span>
                                    </div>
                                    <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/accept-invite?token=${inv.token}`); setToast({ message: 'Link copied!', type: 'info' }); }}
                                        style={{ ...btnSecondary, padding: '4px 10px', fontSize: 11 }}>Copy Link</button>
                                    <button onClick={async () => { await api.revokeInvitation(inv.id); api.getInvitations().then(setInvitations); }}
                                        style={{ background: 'none', border: 'none', color: C.danger, cursor: 'pointer', fontSize: 12 }}>Revoke</button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Add User Modal */}
                    {showAddUser && (
                        <Modal title="Add User Directly" onClose={() => setShowAddUser(false)} width={420}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <div><label style={labelStyle}>Email *</label><input style={inputStyle} value={addUserForm.email} onChange={e => setAddUserForm(f => ({ ...f, email: e.target.value }))} /></div>
                                <div><label style={labelStyle}>Name</label><input style={inputStyle} value={addUserForm.name} onChange={e => setAddUserForm(f => ({ ...f, name: e.target.value }))} /></div>
                                <div><label style={labelStyle}>Temporary Password *</label><input style={inputStyle} type="password" value={addUserForm.password} onChange={e => setAddUserForm(f => ({ ...f, password: e.target.value }))} /></div>
                                <div><label style={labelStyle}>Role</label>
                                    <select style={inputStyle} value={addUserForm.role} onChange={e => setAddUserForm(f => ({ ...f, role: e.target.value }))}>
                                        <option value="admin">Admin — Full access + manage users</option>
                                        <option value="viewer">Viewer — Read-only, view stats</option>
                                    </select>
                                </div>
                                <button onClick={directAdd} style={btnPrimary}>Add User</button>
                            </div>
                        </Modal>
                    )}

                    {/* Invite Modal */}
                    {showInvite && (
                        <Modal title="Invite by Email" onClose={() => setShowInvite(false)} width={420}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <div><label style={labelStyle}>Email *</label><input style={inputStyle} placeholder="user@company.com" value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} /></div>
                                <div><label style={labelStyle}>Role</label>
                                    <select style={inputStyle} value={inviteForm.role} onChange={e => setInviteForm(f => ({ ...f, role: e.target.value }))}>
                                        <option value="admin">Admin</option><option value="viewer">Viewer</option>
                                    </select>
                                </div>
                                <div style={{ fontSize: 12, color: C.muted }}>An invitation link will be generated. Share it with the user to join.</div>
                                <button onClick={invite} style={btnPrimary}>Create Invitation</button>
                            </div>
                        </Modal>
                    )}
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════
//  SETTINGS
// ═══════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════
//  LIST CLEANING PAGE
// ═══════════════════════════════════════════════════════════
function ListCleaningPage() {
    const [file, setFile] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [emails, setEmails] = useState([]);
    const [jobId, setJobId] = useState(null);
    const [progress, setProgress] = useState(null);
    const [results, setResults] = useState(null);
    const [toast, setToast] = useState(null);
    const [resultFilter, setResultFilter] = useState('all');
    const [filteredResults, setFilteredResults] = useState(null);
    const pollRef = useRef(null);
    // Import to list
    const [showImport, setShowImport] = useState(false);
    const [lists, setLists] = useState([]);
    const [importListId, setImportListId] = useState('');
    const [newListName, setNewListName] = useState('');
    const [importFilter, setImportFilter] = useState('valid');
    const [importing, setImporting] = useState(false);

    useEffect(() => { api.getLists().then(setLists).catch(() => {}); }, []);

    // Parse CSV file
    const handleFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        setParsing(true);
        setEmails([]);
        setJobId(null);
        setProgress(null);
        setResults(null);

        const reader = new FileReader();
        reader.onload = (ev) => {
            const text = ev.target.result;
            const lines = text.split(/\r?\n/).filter(Boolean);
            const extracted = [];
            const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

            for (const line of lines) {
                const matches = line.match(emailRegex);
                if (matches) extracted.push(...matches);
            }

            const unique = [...new Set(extracted.map(e => e.toLowerCase().trim()))];
            setEmails(unique);
            setParsing(false);
        };
        reader.readAsText(f);
    };

    // Submit for verification
    const submit = async () => {
        if (emails.length === 0) return;
        try {
            setToast({ message: `Submitting ${emails.length} emails for verification...`, type: 'info' });
            const data = await api.submitListCleaning(emails);
            setJobId(data.job_id);
            setProgress({ completed: 0, total: data.total, progress: 0, status: 'processing' });

            // Start polling
            pollRef.current = setInterval(async () => {
                try {
                    const status = await api.getCleaningStatus(data.job_id);
                    setProgress(status);
                    if (status.status === 'completed') {
                        clearInterval(pollRef.current);
                        setResults(status.summary);
                        setToast({ message: `Verification complete! ${status.summary.valid} valid emails found.`, type: 'success' });
                    } else if (status.status === 'failed') {
                        clearInterval(pollRef.current);
                        setToast({ message: `Verification failed: ${status.error}`, type: 'error' });
                    }
                } catch {}
            }, 2000);
        } catch (err) {
            setToast({ message: err.message, type: 'error' });
        }
    };

    // Load filtered results
    const loadResults = async (filter) => {
        if (!jobId) return;
        setResultFilter(filter);
        try {
            const data = await api.getCleaningResults(jobId, filter);
            setFilteredResults(data.results);
        } catch (err) {
            setToast({ message: err.message, type: 'error' });
        }
    };

    // Cleanup polling on unmount
    useEffect(() => {
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, []);

    const statCard = (label, value, color, icon) => (
        <div style={{ flex: '1 1 100px', padding: '14px 16px', background: C.hover, borderRadius: 10, textAlign: 'center', cursor: results ? 'pointer' : 'default',
            border: `2px solid ${resultFilter === label.toLowerCase() ? C.accent : 'transparent'}` }}
            onClick={() => results && loadResults(label.toLowerCase())}>
            <div style={{ fontSize: 22 }}>{icon}</div>
            <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'monospace', color, marginTop: 2 }}>{value}</div>
            <div style={{ fontSize: 11, color: C.muted }}>{label}</div>
        </div>
    );

    return (
        <div>
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}

            {/* Import to List Modal */}
            {showImport && (
                <Modal title="Create List from Verified Emails" onClose={() => setShowImport(false)} width={480}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div>
                            <label style={labelStyle}>Import which emails?</label>
                            <select style={inputStyle} value={importFilter} onChange={e => setImportFilter(e.target.value)}>
                                <option value="valid">✅ Valid only ({results?.valid || 0})</option>
                                <option value="risky">⚠️ Valid + Risky ({(results?.valid || 0) + (results?.risky || 0)})</option>
                                <option value="all">All ({results?.total || 0})</option>
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Add to existing list</label>
                            <select style={inputStyle} value={importListId} onChange={e => { setImportListId(e.target.value); if (e.target.value) setNewListName(''); }}>
                                <option value="">— Create a new list —</option>
                                {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                        </div>
                        {!importListId && (
                            <div>
                                <label style={labelStyle}>New list name</label>
                                <input style={inputStyle} placeholder="e.g. Cleaned Health List - Feb 2026" value={newListName} onChange={e => setNewListName(e.target.value)} />
                            </div>
                        )}
                        <button disabled={importing || (!importListId && !newListName)} onClick={async () => {
                            setImporting(true);
                            try {
                                const r = await api.importCleanedList(jobId, importFilter === 'risky' ? 'valid' : importFilter, importListId || null, importListId ? null : newListName);
                                let total = r.imported;
                                if (importFilter === 'risky') {
                                    const r2 = await api.importCleanedList(jobId, 'risky', r.list_id || importListId, null);
                                    total += r2.imported;
                                }
                                setToast({ message: `Imported ${total} contacts into list!`, type: 'success' });
                                setShowImport(false);
                                api.getLists().then(setLists).catch(() => {});
                            } catch (e) { setToast({ message: e.message, type: 'error' }); }
                            finally { setImporting(false); }
                        }} style={{ ...btnPrimary, opacity: (!importListId && !newListName) || importing ? 0.5 : 1 }}>
                            {importing ? '⏳ Importing...' : '📋 Import to List'}
                        </button>
                    </div>
                </Modal>
            )}

            <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700 }}>🧹 List Cleaning</h2>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>Upload a CSV or text file with email addresses. We'll verify each one via SMTP to check if the mailbox actually exists.</div>

            {/* Upload */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 20 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ ...btnPrimary, cursor: 'pointer', display: 'inline-block' }}>
                        📂 Choose CSV File
                        <input type="file" accept=".csv,.txt,.tsv" onChange={handleFile} style={{ display: 'none' }} />
                    </label>
                    {file && <span style={{ fontSize: 13, color: C.muted }}>{file.name}</span>}
                    {parsing && <span style={{ fontSize: 13, color: C.accent }}>Parsing...</span>}
                    {emails.length > 0 && !jobId && (
                        <>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>{emails.length.toLocaleString()} unique emails found</span>
                            <button onClick={submit} style={btnPrimary}>🚀 Start Verification</button>
                        </>
                    )}
                </div>
                {emails.length > 0 && !jobId && (
                    <div style={{ marginTop: 12, fontSize: 12, color: C.dim }}>
                        Preview: {emails.slice(0, 5).join(', ')}{emails.length > 5 ? `, ... and ${emails.length - 5} more` : ''}
                    </div>
                )}
            </div>

            {/* Progress */}
            {progress && progress.status === 'processing' && (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>⏳ Verifying...</span>
                        <span style={{ fontSize: 13, fontFamily: 'monospace' }}>{progress.completed} / {progress.total}</span>
                    </div>
                    <div style={{ height: 8, borderRadius: 4, background: C.hover, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${progress.progress}%`, background: C.accent, borderRadius: 4, transition: 'width 0.3s ease' }} />
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 6, textAlign: 'right' }}>{progress.progress}% complete</div>
                </div>
            )}

            {/* Results Summary */}
            {results && (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Verification Results</h3>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => api.downloadCleanedCSV(jobId, 'valid')} style={btnPrimary}>📥 Download Valid Only</button>
                            <button onClick={() => api.downloadCleanedCSV(jobId, 'all')} style={btnSecondary}>📥 Download All</button>
                            <button onClick={() => { setShowImport(true); setImportFilter('valid'); setImportListId(''); setNewListName(''); }} style={{ ...btnSecondary, background: '#1e1b4b', color: '#a78bfa', borderColor: '#4c1d95' }}>📋 Create List from Results</button>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                        {statCard('Valid', results.valid, C.success, '✅')}
                        {statCard('Invalid', results.invalid, C.danger, '❌')}
                        {statCard('Risky', results.risky, '#f59e0b', '⚠️')}
                        {statCard('Unknown', results.unknown, C.muted, '❓')}
                    </div>

                    <div style={{ display: 'flex', gap: 16, fontSize: 12, color: C.muted, flexWrap: 'wrap' }}>
                        <span>📧 Free providers: {results.free}</span>
                        <span>🎭 Role-based: {results.role}</span>
                        <span>🗑 Disposable: {results.disposable}</span>
                        <span>🌐 Catch-all: {results.catch_all}</span>
                    </div>

                    <div style={{ fontSize: 11, color: C.dim, marginTop: 8 }}>Click a category above to view those emails</div>
                </div>
            )}

            {/* Filtered Results Table */}
            {filteredResults && (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>
                            {resultFilter === 'all' ? 'All' : resultFilter.charAt(0).toUpperCase() + resultFilter.slice(1)} emails ({filteredResults.length})
                        </span>
                        <button onClick={() => api.downloadCleanedCSV(jobId, resultFilter)} style={{ ...btnSecondary, padding: '4px 10px', fontSize: 11 }}>📥 Download these</button>
                    </div>
                    <div style={{ maxHeight: 400, overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: `1px solid ${C.border}`, position: 'sticky', top: 0, background: C.card }}>
                                    {['Email', 'Result', 'Reason', 'MX', 'Flags'].map(h => (
                                        <th key={h} style={{ padding: '8px 12px', fontSize: 10, color: C.muted, textAlign: 'left', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredResults.slice(0, 500).map((r, i) => (
                                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                                        <td style={{ padding: '6px 12px', fontSize: 12, fontFamily: 'monospace' }}>{r.email}</td>
                                        <td style={{ padding: '6px 12px' }}>
                                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                                                background: r.result === 'valid' ? C.successDim : r.result === 'invalid' ? C.dangerDim : r.result === 'risky' ? '#fef3c7' : C.hover,
                                                color: r.result === 'valid' ? C.success : r.result === 'invalid' ? C.danger : r.result === 'risky' ? '#d97706' : C.muted }}>
                                                {r.result}
                                            </span>
                                        </td>
                                        <td style={{ padding: '6px 12px', fontSize: 11, color: C.muted, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.reason}</td>
                                        <td style={{ padding: '6px 12px', fontSize: 11, fontFamily: 'monospace', color: C.dim }}>{r.mx || '—'}</td>
                                        <td style={{ padding: '6px 12px', fontSize: 10 }}>
                                            {r.is_free && <span title="Free provider" style={{ marginRight: 4 }}>📧</span>}
                                            {r.is_role && <span title="Role-based" style={{ marginRight: 4 }}>🎭</span>}
                                            {r.is_disposable && <span title="Disposable" style={{ marginRight: 4 }}>🗑</span>}
                                            {r.is_catch_all && <span title="Catch-all" style={{ marginRight: 4 }}>🌐</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredResults.length > 500 && <div style={{ padding: 12, textAlign: 'center', fontSize: 11, color: C.muted }}>Showing first 500 of {filteredResults.length}. Download CSV for full list.</div>}
                    </div>
                </div>
            )}

            {/* Instructions when empty */}
            {!file && !jobId && (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 40, textAlign: 'center' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Upload a list to get started</div>
                    <div style={{ fontSize: 12, color: C.muted, maxWidth: 400, margin: '0 auto', lineHeight: 1.6 }}>
                        Upload any CSV, TXT, or TSV file containing email addresses. We'll automatically extract all emails and verify each one by connecting to the recipient's mail server to check if the mailbox actually exists — just like ZeroBounce.
                    </div>
                    <div style={{ marginTop: 16, display: 'flex', gap: 16, justifyContent: 'center', fontSize: 12, color: C.dim }}>
                        <span>✅ Syntax check</span>
                        <span>🔍 MX record lookup</span>
                        <span>📡 SMTP mailbox verification</span>
                        <span>🌐 Catch-all detection</span>
                        <span>🗑 Disposable filter</span>
                    </div>
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
//  TEMPLATES PAGE
// ═══════════════════════════════════════════════════════════
function TemplatesPage() {
    const [templates, setTemplates] = useState([]);
    const [editing, setEditing] = useState(null);
    const [showEditor, setShowEditor] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [form, setForm] = useState({ name: '', subject: '', html_body: '', text_body: '', format: 'html', category: 'general' });
    const [toast, setToast] = useState(null);

    const load = useCallback(() => { api.getTemplates().then(setTemplates).catch(() => {}); }, []);
    useEffect(load, [load]);

    const save = async () => {
        try {
            if (!form.name) { setToast({ message: 'Template name required', type: 'error' }); return; }
            if (editing) { await api.updateTemplate(editing, form); }
            else { await api.createTemplate(form); }
            setEditing(null); setShowEditor(false); setForm({ name: '', subject: '', html_body: '', text_body: '', format: 'html', category: 'general' });
            setToast({ message: editing ? 'Template updated' : 'Template created', type: 'success' }); load();
        } catch (e) { setToast({ message: e.message, type: 'error' }); }
    };

    return (
        <div>
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
            {showPreview && (
                <EmailPreviewModal subject={form.subject} html={form.format === 'text' ? '' : form.html_body} text={form.text_body} onClose={() => setShowPreview(false)} />
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>📋 Email Templates</h2>
                <button onClick={() => { setEditing(null); setForm({ name: '', subject: '', html_body: '', text_body: '', format: 'html', category: 'general' }); setShowEditor(true); }}
                    style={btnPrimary}>+ New Template</button>
            </div>

            {/* Editor */}
            {showEditor && (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <div><label style={labelStyle}>Template Name</label><input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Welcome Email" /></div>
                        <div><label style={labelStyle}>Subject Line</label><input style={inputStyle} value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Subject line" /></div>
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                        <div><label style={labelStyle}>Format</label>
                            <select style={inputStyle} value={form.format} onChange={e => setForm({ ...form, format: e.target.value })}>
                                <option value="html">HTML</option><option value="text">Text</option><option value="both">Both</option>
                            </select>
                        </div>
                        <div><label style={labelStyle}>Category</label>
                            <select style={inputStyle} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                                <option value="general">General</option><option value="outreach">Outreach</option><option value="newsletter">Newsletter</option><option value="follow-up">Follow-up</option>
                            </select>
                        </div>
                    </div>
                    {form.format !== 'text' && (
                        <div style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                                <label style={{ ...labelStyle, marginBottom: 0 }}>HTML Body</label>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <HtmlImportButton onImport={(html, fileName) => {
                                        setForm(current => ({ ...current, html_body: html }));
                                        setToast({ message: `${fileName} imported`, type: 'success' });
                                    }} onError={message => setToast({ message, type: 'error' })} />
                                    <button type="button" onClick={() => setShowPreview(true)}
                                        style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11 }}>◎ Preview</button>
                                </div>
                            </div>
                            <textarea style={{ ...inputStyle, height: 200, fontFamily: 'monospace', fontSize: 13 }} value={form.html_body} onChange={e => setForm({ ...form, html_body: e.target.value })} placeholder="<html>...</html>" />
                        </div>
                    )}
                    {(form.format === 'text' || form.format === 'both') && (
                        <div style={{ marginBottom: 12 }}>
                            <label style={labelStyle}>Text Body</label>
                            <textarea style={{ ...inputStyle, height: 150, fontFamily: 'monospace', fontSize: 13 }} value={form.text_body} onChange={e => setForm({ ...form, text_body: e.target.value })} placeholder="Plain text version..." />
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={save} style={btnPrimary}>{editing ? '💾 Update' : '💾 Save Template'}</button>
                        <button onClick={() => { setEditing(null); setShowEditor(false); setForm({ name: '', subject: '', html_body: '', text_body: '', format: 'html', category: 'general' }); }} style={btnSecondary}>Cancel</button>
                    </div>
                </div>
            )}

            {/* List */}
            {templates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>No templates yet. Create one above or save a campaign as a template.</div>
            ) : (
                templates.map(t => (
                    <div key={t.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</div>
                            <div style={{ fontSize: 11, color: C.muted }}>{t.subject || 'No subject'} — {t.format} — {t.category} — {new Date(t.updated_at).toLocaleDateString()}</div>
                        </div>
                        <button onClick={() => { setEditing(t.id); setShowEditor(true); setForm({ name: t.name, subject: t.subject || '', html_body: t.html_body || '', text_body: t.text_body || '', format: t.format || 'html', category: t.category || 'general' }); }} style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11 }}>Edit</button>
                        <button onClick={async () => { if (window.confirm('Delete this template?')) { await api.deleteTemplate(t.id); load(); } }} style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11, color: C.danger }}>Delete</button>
                    </div>
                ))
            )}
        </div>
    );
}

function SettingsPage() {
    const [settings, setSettings] = useState({});
    const [toast, setToast] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => { api.getSettings().then(setSettings).catch(() => {}); }, []);

    const save = async () => {
        setSaving(true);
        try { await api.updateSettings(settings); setToast({ message: 'Settings saved', type: 'success' }); }
        catch (err) { setToast({ message: err.message, type: 'error' }); }
        setSaving(false);
    };

    const update = (key, val) => setSettings(s => ({ ...s, [key]: val }));

    return (
        <div style={{ maxWidth: 700 }}>
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600 }}>Google OAuth (for Gmail workspaces)</h3>
                <p style={{ margin: '0 0 16px', fontSize: 12, color: C.muted }}>Only needed if connecting Gmail accounts. Get credentials from <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" style={{ color: C.accent }}>Google Cloud Console</a></p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div><label style={labelStyle}>Client ID</label><input style={inputStyle} value={settings.google_client_id || ''} onChange={e => update('google_client_id', e.target.value)} /></div>
                    <div><label style={labelStyle}>Client Secret</label><input style={inputStyle} type="password" value={settings.google_client_secret || ''} onChange={e => update('google_client_secret', e.target.value)} /></div>
                </div>
            </div>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Sending Defaults</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div><label style={labelStyle}>Default Daily Limit (per workspace)</label><input style={inputStyle} type="number" value={settings.default_daily_limit || 500} onChange={e => update('default_daily_limit', e.target.value)} /></div>
                    <div><label style={labelStyle}>Tracking Domain</label><input style={inputStyle} value={settings.tracking_domain || ''} onChange={e => update('tracking_domain', e.target.value)} placeholder="https://trk.yourdomain.com" /></div>
                </div>
            </div>
            <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving...' : 'Save Settings'}</button>
        </div>
    );
}

// ═══════════════════════════════════════════════════════
//  INBOX PLACEMENT TESTER
// ═══════════════════════════════════════════════════════

function InboxTestPage() {
    const [seeds, setSeeds] = useState([]);
    const [workspaces, setWorkspaces] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [recipientLists, setRecipientLists] = useState([]);
    const [history, setHistory] = useState([]);
    const [testTracking, setTestTracking] = useState([]);
    const [showAddSeed, setShowAddSeed] = useState(false);
    const [showImportSeeds, setShowImportSeeds] = useState(false);
    const [seedForm, setSeedForm] = useState({ provider: 'gmail' });
    const [seedCsvData, setSeedCsvData] = useState(null);
    const [testForm, setTestForm] = useState({ subject: '', html_body: '', text_body: '', template_id: '', list_id: '', recipient_mode: 'seeds' });
    const [manualRecipients, setManualRecipients] = useState('');
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [showTestPreview, setShowTestPreview] = useState(false);
    const [toast, setToast] = useState(null);

    const refreshTestAnalytics = useCallback(async () => {
        const [nextHistory, nextTracking] = await Promise.all([
            api.getTestHistory(),
            api.getInboxTestTracking(),
        ]);
        setHistory(nextHistory);
        setTestTracking(nextTracking);
        setTestResult(current => {
            if (!current?.id) return current;
            const updated = nextHistory.find(item => item.id === current.id);
            if (!updated) return current;
            return {
                ...current,
                analytics: {
                    tracked_sent_count: Number(updated.tracked_sent_count || 0),
                    unique_opens: Number(updated.unique_opens || 0),
                    unique_clicks: Number(updated.unique_clicks || 0),
                    total_open_events: Number(updated.total_open_events || 0),
                    total_click_events: Number(updated.total_click_events || 0),
                    open_rate_pct: Number(updated.open_rate_pct || 0),
                    click_rate_pct: Number(updated.click_rate_pct || 0),
                },
            };
        });
    }, []);

    const load = useCallback(() => {
        api.getSeeds().then(setSeeds).catch(() => {});
        api.getWorkspaces().then(setWorkspaces).catch(() => {});
        api.getTemplates().then(setTemplates).catch(() => {});
        api.getInboxTestRecipientLists().then(setRecipientLists).catch(() => {});
        api.getTestHistory().then(setHistory).catch(() => {});
        api.getInboxTestTracking().then(setTestTracking).catch(() => {});
    }, []);
    useEffect(load, [load]);
    useEffect(() => {
        if (!testResult?.id) return undefined;
        const timer = setInterval(() => refreshTestAnalytics().catch(() => {}), 10000);
        return () => clearInterval(timer);
    }, [testResult?.id, refreshTestAnalytics]);

    const addSeed = async () => {
        try {
            await api.addSeed(seedForm);
            setToast({ message: 'Seed account added', type: 'success' });
            setShowAddSeed(false); setSeedForm({ provider: 'gmail' }); load();
        } catch (err) { setToast({ message: err.message, type: 'error' }); }
    };

    const importSeeds = async () => {
        if (!seedCsvData?.length) return;
        let added = 0, failed = 0;
        for (const row of seedCsvData) {
            try {
                await api.addSeed(row);
                added++;
            } catch { failed++; }
        }
        setToast({ message: `Imported ${added} seeds${failed ? `, ${failed} failed` : ''}`, type: 'success' });
        setShowImportSeeds(false); setSeedCsvData(null); load();
    };

    const parseSeedCsv = (text) => {
        const lines = text.split('\n').filter(l => l.trim());
        if (lines.length < 2) return [];
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        return lines.slice(1).map(line => {
            const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
            const row = {};
            headers.forEach((h, i) => { row[h] = vals[i] || ''; });
            // Map common column names
            return {
                provider: row.provider || 'other',
                email: row.email || row.imap_user || '',
                imap_host: row.imap_host || row.host || '',
                imap_port: parseInt(row.imap_port || row.port) || 993,
                imap_user: row.imap_user || row.email || row.username || '',
                imap_password: row.imap_password || row.password || row.pass || '',
            };
        }).filter(r => r.email);
    };

    const runTest = async () => {
        if (!testForm.workspace_id) { setToast({ message: 'Select a workspace', type: 'error' }); return; }
        if (invalidManualEmail) { setToast({ message: `Invalid recipient email: ${invalidManualEmail}`, type: 'error' }); return; }
        if (selectedRecipientCount === 0) {
            const message = testForm.recipient_mode === 'direct'
                ? 'Enter at least one email address or select a contact list'
                : (testForm.list_id ? 'That list has no members with configured seed accounts' : 'Add at least one seed account');
            setToast({ message, type: 'error' });
            return;
        }
        if (testForm.recipient_mode === 'direct' && selectedRecipientCount > 25) {
            setToast({ message: 'Send-only tests are limited to 25 recipients', type: 'error' });
            return;
        }
        setTesting(true); setTestResult(null);
        try {
            const result = await api.runInboxTest({ ...testForm, recipient_emails: manualRecipientEmails });
            setTestResult({
                ...result,
                analytics: {
                    tracked_sent_count: Number(result.summary?.sent || 0),
                    unique_opens: 0,
                    unique_clicks: 0,
                    total_open_events: 0,
                    total_click_events: 0,
                    open_rate_pct: 0,
                    click_rate_pct: 0,
                },
            });
            load();
        } catch (err) { setToast({ message: err.message, type: 'error' }); }
        setTesting(false);
    };

    const placementColor = (folder) => {
        if (folder === 'inbox') return C.success;
        if (folder === 'sent') return C.success;
        if (folder === 'send_failed') return C.danger;
        if (folder === 'spam' || folder === 'junk') return C.danger;
        if (folder === 'promotions') return C.warning;
        if (folder === 'not_found') return C.dim;
        return C.muted;
    };
    const placementIcon = (folder) => {
        if (folder === 'inbox') return '✅';
        if (folder === 'sent') return '📤';
        if (folder === 'send_failed') return '❌';
        if (folder === 'spam' || folder === 'junk') return '🚫';
        if (folder === 'promotions') return '📢';
        if (folder === 'not_found') return '❓';
        return '⚠';
    };

    const providers = ['gmail', 'yahoo', 'outlook', 'icloud', 'aol', 'zoho', 'other'];
    const selectedTemplate = templates.find(template => template.id === testForm.template_id);
    const selectedRecipientList = recipientLists.find(list => list.id === testForm.list_id);
    const manualRecipientEmails = [...new Set(manualRecipients.split(/[\s,;]+/).map(email => email.trim().toLowerCase()).filter(Boolean))];
    const invalidManualEmail = manualRecipientEmails.find(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    const selectedRecipientCount = testForm.recipient_mode === 'direct'
        ? Number(selectedRecipientList?.active_contact_count ?? selectedRecipientList?.contact_count ?? 0) + manualRecipientEmails.length
        : (selectedRecipientList ? Number(selectedRecipientList.seed_count || 0) : seeds.length);
    const directRecipientLimitExceeded = testForm.recipient_mode === 'direct' && selectedRecipientCount > 25;

    const selectTemplate = templateId => {
        const template = templates.find(item => item.id === templateId);
        if (!template) {
            setTestForm(current => ({ ...current, template_id: '' }));
            return;
        }
        setTestForm(current => ({
            ...current,
            template_id: templateId,
            subject: template?.subject || current.subject,
            html_body: template?.html_body || '',
            text_body: template?.text_body || '',
        }));
    };

    return (
        <div style={{ maxWidth: 900 }}>
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
            {showTestPreview && (
                <EmailPreviewModal subject={testForm.subject} html={testForm.html_body} text={testForm.text_body} onClose={() => setShowTestPreview(false)} />
            )}
            <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700 }}>Inbox Placement Test</h2>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: C.muted }}>
                Check placement with configured seeds, or send a template directly to your own test recipients without mailbox credentials.
            </p>

            {/* Seed Accounts */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Seed Accounts ({seeds.length})</h3>
                    <button onClick={() => setShowAddSeed(true)} style={{ ...btnSecondary, padding: '6px 14px', fontSize: 12 }}>+ Add Seed</button>
                    <button onClick={() => setShowImportSeeds(true)} style={{ ...btnSecondary, padding: '6px 14px', fontSize: 12 }}>📥 Import CSV</button>
                </div>
                {seeds.length === 0 ? (
                    <div style={{ padding: 20, textAlign: 'center', color: C.dim, fontSize: 13 }}>
                        No seed accounts. Add Gmail, Yahoo, Outlook accounts to test inbox placement.
                    </div>
                ) : (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {seeds.map(s => (
                            <div key={s.id} style={{ padding: '8px 12px', background: C.hover, borderRadius: 8, fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ textTransform: 'uppercase', fontWeight: 700, fontSize: 10, color: C.accent }}>{s.provider}</span>
                                <span style={{ color: C.muted }}>{s.email}</span>
                                <span style={{ color: s.imap_host ? C.success : C.warning, fontSize: 10 }}>{s.imap_host ? 'IMAP ✓' : 'No IMAP'}</span>
                                <button onClick={async () => { await api.deleteSeed(s.id); load(); }}
                                    style={{ background: 'none', border: 'none', color: C.danger, cursor: 'pointer', fontSize: 11 }}>✕</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Run Test */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>Run Test</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div><label style={labelStyle}>Send From (Workspace)</label>
                        <select style={inputStyle} value={testForm.workspace_id || ''} onChange={e => setTestForm(f => ({ ...f, workspace_id: e.target.value }))}>
                            <option value="">Select workspace...</option>
                            {workspaces.map(w => <option key={w.id} value={w.id}>{w.display_name || w.email} ({w.provider})</option>)}
                        </select>
                    </div>
                    <div><label style={labelStyle}>Test Type</label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            {[
                                { key: 'seeds', title: 'Inbox placement', detail: 'Uses configured seed mailboxes and IMAP scoring' },
                                { key: 'direct', title: 'Send-only test', detail: 'Sends to ordinary addresses; no placement scoring' },
                            ].map(mode => (
                                <button key={mode.key} type="button" onClick={() => setTestForm(f => ({ ...f, recipient_mode: mode.key, list_id: '' }))}
                                    style={{ padding: 12, textAlign: 'left', borderRadius: 9, cursor: 'pointer', color: C.text,
                                        background: testForm.recipient_mode === mode.key ? C.accentDim : C.hover,
                                        border: `1px solid ${testForm.recipient_mode === mode.key ? C.accent : C.border}` }}>
                                    <div style={{ fontSize: 12, fontWeight: 700 }}>{mode.title}</div>
                                    <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>{mode.detail}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div><label style={labelStyle}>Recipients</label>
                        <select style={inputStyle} value={testForm.list_id} onChange={e => setTestForm(f => ({ ...f, list_id: e.target.value }))}>
                            <option value="">{testForm.recipient_mode === 'direct' ? 'Manual email addresses only' : `All configured seed accounts (${seeds.length})`}</option>
                            {recipientLists.map(list => {
                                const count = Number(testForm.recipient_mode === 'direct' ? (list.active_contact_count ?? list.contact_count) : list.seed_count) || 0;
                                const tooLarge = testForm.recipient_mode === 'direct' && count > 25;
                                return (
                                    <option key={list.id} value={list.id} disabled={count === 0 || tooLarge}>
                                        {testForm.recipient_mode === 'direct'
                                            ? `${list.name} (${count} active contacts${tooLarge ? ' — over 25 limit' : ''})`
                                            : `${list.name} (${count} configured seeds / ${list.contact_count} contacts)`}
                                    </option>
                                );
                            })}
                        </select>
                        <div style={{ fontSize: 10, color: C.dim, marginTop: 5 }}>
                            {testForm.recipient_mode === 'direct'
                                ? 'Send-only tests support up to 25 unique recipients. Only active contacts are included from a list.'
                                : 'Lists only include members whose email matches a configured seed account, because placement checks require mailbox access.'}
                        </div>
                    </div>
                    {testForm.recipient_mode === 'direct' && (
                        <div><label style={labelStyle}>Additional Email Addresses</label>
                            <textarea style={{ ...inputStyle, minHeight: 76, fontFamily: 'monospace', fontSize: 12 }}
                                value={manualRecipients} onChange={e => setManualRecipients(e.target.value)}
                                placeholder="you@example.com, friend@example.com" />
                            <div style={{ fontSize: 10, color: invalidManualEmail ? C.danger : C.dim, marginTop: 5 }}>
                                {invalidManualEmail ? `Invalid address: ${invalidManualEmail}` : 'Separate addresses with commas, spaces, semicolons, or new lines.'}
                            </div>
                        </div>
                    )}
                    <div><label style={labelStyle}>Email Content</label>
                        <select style={inputStyle} value={testForm.template_id} onChange={e => selectTemplate(e.target.value)}>
                            <option value="">Custom content</option>
                            {templates.map(template => <option key={template.id} value={template.id}>{template.name} — {template.format}</option>)}
                        </select>
                    </div>
                    {selectedTemplate ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, background: C.hover, border: `1px solid ${C.border}`, borderRadius: 9 }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 600 }}>{selectedTemplate.name}</div>
                                <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{testForm.subject || 'No subject'} · {selectedTemplate.format}</div>
                            </div>
                            <button type="button" onClick={() => setShowTestPreview(true)} style={{ ...btnSecondary, padding: '7px 12px', fontSize: 11 }}>◎ Preview</button>
                        </div>
                    ) : (
                        <>
                            <div><label style={labelStyle}>Subject Line (optional)</label>
                                <input style={inputStyle} value={testForm.subject} onChange={e => setTestForm(f => ({ ...f, subject: e.target.value }))} placeholder="Test subject line" /></div>
                            <div><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                <label style={{ ...labelStyle, marginBottom: 0 }}>Email Body HTML (optional — uses default test content if empty)</label>
                                <button type="button" onClick={() => setShowTestPreview(true)} style={{ ...btnSecondary, padding: '5px 10px', fontSize: 10 }}>◎ Preview</button>
                            </div>
                                <textarea style={{ ...inputStyle, minHeight: 100, fontFamily: 'monospace', fontSize: 12 }}
                                    value={testForm.html_body} onChange={e => setTestForm(f => ({ ...f, html_body: e.target.value }))} placeholder="<div>Your HTML...</div>" /></div>
                        </>
                    )}
                    <button onClick={runTest} disabled={testing || selectedRecipientCount === 0 || !!invalidManualEmail || directRecipientLimitExceeded} style={{ ...btnPrimary, opacity: testing || selectedRecipientCount === 0 || invalidManualEmail || directRecipientLimitExceeded ? 0.6 : 1 }}>
                        {testing
                            ? (testForm.recipient_mode === 'direct' ? '⏳ Sending test...' : '⏳ Sending & checking (60-90s)...')
                            : (testForm.recipient_mode === 'direct'
                                ? `📤 Send Test (${selectedRecipientCount} recipient${selectedRecipientCount === 1 ? '' : 's'})`
                                : `🧪 Test Placement (${selectedRecipientCount} seed${selectedRecipientCount === 1 ? '' : 's'})`)}
                    </button>
                </div>
            </div>

            {/* Test Results */}
            {testResult && (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Results</h3>
                    <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                        {(testResult.recipient_mode === 'direct'
                            ? [{ label: 'Sent', count: testResult.summary?.sent, color: C.success },
                               { label: 'Failed', count: testResult.summary?.errors, color: C.danger }]
                            : [{ label: 'Inbox', count: testResult.summary?.inbox, color: C.success },
                               { label: 'Spam', count: testResult.summary?.spam, color: C.danger },
                               { label: 'Promos', count: testResult.summary?.promotions, color: C.warning },
                               { label: 'Missing', count: testResult.summary?.not_found, color: C.dim }])
                          .filter(x => x.count > 0).map(x => (
                            <div key={x.label} style={{ padding: '12px 20px', background: C.hover, borderRadius: 8, textAlign: 'center' }}>
                                <div style={{ fontSize: 28, fontWeight: 700, color: x.color, fontFamily: 'monospace' }}>{x.count}</div>
                                <div style={{ fontSize: 11, color: C.muted }}>{x.label}</div>
                            </div>
                        ))}
                    </div>
                    {testResult.results?.map((r, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < testResult.results.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                            <span style={{ fontSize: 18 }}>{placementIcon(r.folder)}</span>
                            <span style={{ textTransform: 'uppercase', fontWeight: 700, fontSize: 10, color: C.accent, width: 60 }}>{r.provider}</span>
                            <span style={{ fontSize: 12, color: C.muted, flex: 1, fontFamily: 'monospace' }}>{r.email}</span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: placementColor(r.folder) }}>{r.folder}</span>
                        </div>
                    ))}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 10, marginTop: 16 }}>
                        {[
                            { label: 'Unique opens', value: `${Number(testResult.analytics?.unique_opens || 0)}/${Number(testResult.analytics?.tracked_sent_count ?? testResult.summary?.sent ?? 0)}`, color: C.info },
                            { label: 'Open rate', value: `${Number(testResult.analytics?.open_rate_pct || 0).toFixed(1)}%`, color: C.info },
                            { label: 'Unique clicks', value: Number(testResult.analytics?.unique_clicks || 0), color: C.accent },
                            { label: 'CTR', value: `${Number(testResult.analytics?.click_rate_pct || 0).toFixed(1)}%`, color: C.accent },
                        ].map(metric => (
                            <div key={metric.label} style={{ padding: 12, background: C.hover, borderRadius: 8, textAlign: 'center' }}>
                                <div style={{ fontSize: 20, fontWeight: 700, color: metric.color, fontFamily: 'monospace' }}>{metric.value}</div>
                                <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>{metric.label}</div>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                        <button type="button" onClick={() => refreshTestAnalytics().catch(err => setToast({ message: err.message, type: 'error' }))}
                            style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11 }}>↻ Refresh analytics</button>
                    </div>
                </div>
            )}

            {/* Test-only tracking diagnostics */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Tracking verification</h3>
                        <div style={{ fontSize: 10, color: C.dim, marginTop: 4 }}>Open the test email, then refresh. Test events never change campaign open rates.</div>
                    </div>
                    <button type="button" onClick={() => refreshTestAnalytics().catch(err => setToast({ message: err.message, type: 'error' }))}
                        style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11 }}>↻ Refresh</button>
                </div>
                {testTracking.length === 0 ? (
                    <div style={{ padding: '12px 0', color: C.dim, fontSize: 12 }}>No tracked test emails yet.</div>
                ) : testTracking.map(item => (
                    <div key={item.id} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12, alignItems: 'center', padding: '10px 0', borderTop: `1px solid ${C.border}`, fontSize: 12 }}>
                        <span><b>{item.recipient_email || 'Test recipient'}</b><br/><small style={{ color: C.dim }}>{item.subject || 'No subject'}</small></span>
                        <span style={{ color: C.muted }}>{item.workspace_email}<br/><small>{item.test_source === 'campaign_test' ? 'Campaign test' : 'Inbox test'}</small></span>
                        <span style={{ color: item.pixel_loaded ? C.success : item.status === 'test_failed' ? C.danger : C.warning, fontWeight: 700 }}>
                            {item.pixel_loaded ? '● Pixel loaded' : item.status === 'test_failed' ? '● Send failed' : '○ Waiting'}
                        </span>
                        <span style={{ color: C.muted }}>{Number(item.click_events || 0)} click{Number(item.click_events || 0) === 1 ? '' : 's'}</span>
                    </div>
                ))}
            </div>

            {/* History */}
            {history.length > 0 && (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>Test History</h3>
                    {history.map(h => {
                        const r = typeof h.results === 'string' ? JSON.parse(h.results) : h.results;
                        const inbox = r?.filter(x => x.folder === 'inbox').length || 0;
                        const sentOnly = r?.filter(x => x.folder === 'sent').length || 0;
                        const total = r?.length || 0;
                        const isSendOnly = total > 0 && sentOnly > 0 && !r.some(x => ['inbox', 'spam', 'promotions', 'not_found'].includes(x.folder));
                        return (
                            <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
                                <span style={{ color: C.muted, fontSize: 11, fontFamily: 'monospace', width: 120 }}>{new Date(h.created_at).toLocaleDateString()}</span>
                                <span style={{ flex: 1 }}>{h.workspace_name || h.workspace_email}</span>
                                <span style={{ fontWeight: 700, color: isSendOnly ? (sentOnly === total ? C.success : C.warning) : inbox === total ? C.success : inbox > 0 ? C.warning : C.danger }}>
                                    {isSendOnly ? `${sentOnly}/${total} sent` : `${inbox}/${total} inbox`}
                                    <br/><small style={{ color: C.muted, fontWeight: 500 }}>
                                        {Number(h.tracked_sent_count || 0) > 0
                                            ? `${Number(h.open_rate_pct || 0).toFixed(1)}% open · ${Number(h.click_rate_pct || 0).toFixed(1)}% CTR`
                                            : 'Tracking unavailable for older test'}
                                    </small>
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Add Seed Modal */}
            {showAddSeed && (
                <Modal title="Add Seed Account" onClose={() => setShowAddSeed(false)} width={500}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div><label style={labelStyle}>Provider</label>
                            <select style={inputStyle} value={seedForm.provider} onChange={e => {
                                const p = e.target.value;
                                const imapDefaults = {
                                    gmail: { host: 'imap.gmail.com', port: 993 },
                                    yahoo: { host: 'imap.mail.yahoo.com', port: 993 },
                                    outlook: { host: 'outlook.office365.com', port: 993 },
                                    icloud: { host: 'imap.mail.me.com', port: 993 },
                                    aol: { host: 'imap.aol.com', port: 993 },
                                    zoho: { host: 'imap.zoho.com', port: 993 },
                                };
                                setSeedForm(f => ({ ...f, provider: p, imap_host: imapDefaults[p]?.host || '', imap_port: imapDefaults[p]?.port || 993 }));
                            }}>
                                {providers.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                            </select></div>
                        <div><label style={labelStyle}>Email Address</label>
                            <input style={inputStyle} value={seedForm.email || ''} onChange={e => setSeedForm(f => ({ ...f, email: e.target.value }))} placeholder="seed-test@gmail.com" /></div>
                        <div style={{ padding: 12, background: C.hover, borderRadius: 8 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: C.accent }}>IMAP Settings (for placement checking)</div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <div style={{ flex: 2 }}><label style={labelStyle}>IMAP Host</label>
                                    <input style={inputStyle} value={seedForm.imap_host || ''} onChange={e => setSeedForm(f => ({ ...f, imap_host: e.target.value }))} /></div>
                                <div style={{ flex: 1 }}><label style={labelStyle}>Port</label>
                                    <input style={inputStyle} type="number" value={seedForm.imap_port || 993} onChange={e => setSeedForm(f => ({ ...f, imap_port: parseInt(e.target.value) }))} /></div>
                            </div>
                            <div style={{ marginTop: 8 }}><label style={labelStyle}>IMAP Password / App Password</label>
                                <input style={inputStyle} type="password" value={seedForm.imap_password || ''} onChange={e => setSeedForm(f => ({ ...f, imap_password: e.target.value }))} /></div>
                            <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>For Gmail: Use an App Password (Settings → Security → 2FA → App Passwords)</div>
                        </div>
                        <button onClick={addSeed} style={btnPrimary}>Add Seed Account</button>
                    </div>
                </Modal>
            )}
            {/* Import Seeds CSV Modal */}
            {showImportSeeds && (
                <Modal title="Import Seed Accounts from CSV" onClose={() => { setShowImportSeeds(false); setSeedCsvData(null); }} width={550}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div style={{ padding: 16, border: `2px dashed ${C.border}`, borderRadius: 8, textAlign: 'center' }}>
                            <input type="file" accept=".csv,.txt" onChange={e => {
                                const f = e.target.files?.[0]; if (!f) return;
                                const reader = new FileReader();
                                reader.onload = (ev) => { setSeedCsvData(parseSeedCsv(ev.target.result)); };
                                reader.readAsText(f);
                            }} />
                            <div style={{ fontSize: 11, color: C.dim, marginTop: 8 }}>
                                CSV columns: <code>provider, email, imap_host, imap_port, imap_user, imap_password</code>
                            </div>
                        </div>
                        {seedCsvData && (
                            <div>
                                <div style={{ color: C.success, fontSize: 13, marginBottom: 8 }}>✓ {seedCsvData.length} seed accounts parsed</div>
                                <div style={{ maxHeight: 200, overflow: 'auto', fontSize: 12, background: C.hover, borderRadius: 8, padding: 10 }}>
                                    {seedCsvData.slice(0, 5).map((s, i) => (
                                        <div key={i} style={{ marginBottom: 4 }}>{s.email} — {s.provider} — {s.imap_host}:{s.imap_port}</div>
                                    ))}
                                    {seedCsvData.length > 5 && <div style={{ color: C.dim }}>...and {seedCsvData.length - 5} more</div>}
                                </div>
                            </div>
                        )}
                        <button onClick={importSeeds} disabled={!seedCsvData?.length} style={{ ...btnPrimary, opacity: seedCsvData?.length ? 1 : 0.5 }}>
                            Import {seedCsvData?.length || 0} Seeds
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════
//  SPAM CHECKER
// ═══════════════════════════════════════════════════════

function SpamCheckerPage() {
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [fromName, setFromName] = useState('');
    const [result, setResult] = useState(null);
    const [checking, setChecking] = useState(false);
    const [toast, setToast] = useState(null);

    const check = async () => {
        setChecking(true);
        try {
            const r = await api.checkSpam({ subject, body, from_name: fromName });
            setResult(r);
        } catch (err) { setToast({ message: err.message, type: 'error' }); }
        setChecking(false);
    };

    const scoreColor = (score) => score >= 80 ? C.success : score >= 60 ? C.warning : C.danger;

    return (
        <div style={{ maxWidth: 900 }}>
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
            <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700 }}>Spam Checker</h2>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: C.muted }}>Test your email content before sending. Checks trigger words, formatting, link density, and more.</p>

            <div style={{ display: 'flex', gap: 16 }}>
                <div style={{ flex: 1 }}>
                    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div><label style={labelStyle}>From Name</label>
                                <input style={inputStyle} value={fromName} onChange={e => setFromName(e.target.value)} placeholder="e.g. Tom from SERVD" /></div>
                            <div><label style={labelStyle}>Subject Line</label>
                                <input style={inputStyle} value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. Quick question about your inventory" /></div>
                            <div><label style={labelStyle}>Email Body (HTML or plain text)</label>
                                <textarea style={{ ...inputStyle, minHeight: 250, resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }}
                                    value={body} onChange={e => setBody(e.target.value)} placeholder="Paste your email content here..." /></div>
                            <button onClick={check} disabled={checking || !body} style={{ ...btnPrimary, opacity: checking || !body ? 0.6 : 1 }}>
                                {checking ? 'Analyzing...' : '🔍 Check for Spam Triggers'}
                            </button>
                        </div>
                    </div>
                </div>

                {result && (
                    <div style={{ width: 340, flexShrink: 0 }}>
                        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, position: 'sticky', top: 24 }}>
                            <div style={{ textAlign: 'center', marginBottom: 20 }}>
                                <HealthRing value={result.score || 0} size={80} />
                                <div style={{ fontSize: 14, fontWeight: 700, color: scoreColor(result.score), marginTop: 8 }}>
                                    {result.score >= 80 ? 'Looking Good!' : result.score >= 60 ? 'Needs Improvement' : 'High Spam Risk'}
                                </div>
                                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Deliverability Score</div>
                            </div>

                            {result.results?.length > 0 && (
                                <div>
                                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: C.danger }}>Issues Found ({result.results.length}):</div>
                                    {result.results.map((issue, i) => (
                                        <div key={i} style={{ display: 'flex', gap: 8, padding: '8px 0', borderBottom: i < result.results.length - 1 ? `1px solid ${C.border}` : 'none', fontSize: 12 }}>
                                            <span style={{ color: issue.severity === 'fail' ? C.danger : issue.severity === 'warn' ? C.warning : C.success, flexShrink: 0 }}>
                                                {issue.severity === 'fail' ? '🔴' : issue.severity === 'warn' ? '🟡' : '🟢'}
                                            </span>
                                            <div>
                                                <div style={{ fontWeight: 500, color: C.text }}>{issue.title}</div>
                                                {issue.description && <div style={{ color: C.muted, marginTop: 2 }}>{issue.description}</div>}
                                                {issue.points && <div style={{ color: C.dim, marginTop: 2, fontFamily: 'monospace', fontSize: 11 }}>{issue.points} points</div>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {(!result.results || result.results.length === 0) && (
                                <div style={{ textAlign: 'center', padding: 16, color: C.success, fontSize: 13 }}>
                                    No spam triggers detected. Your email looks clean!
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════
//  LAYOUT
// ═══════════════════════════════════════════════════════

function Layout({ children }) {
    const { user, logout } = useAuth();
    const location = useLocation();
    const [accounts, setAccounts] = useState([]);
    const [currentAccount, setCurrentAccount] = useState(null);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    useEffect(() => {
        api.getMyAccounts().then(accts => {
            setAccounts(accts);
            const activeId = localStorage.getItem('relay_account_id');
            setCurrentAccount(accts.find(a => a.id === activeId) || accts[0]);
        }).catch(() => {});
    }, []);

    const navItems = [
        { path: '/', label: 'Dashboard', icon: '⊞' },
        { path: '/audience-analytics', label: 'List Analytics', icon: '◫' },
        { path: '/campaigns', label: 'Campaigns', icon: '✉' },
        { path: '/operations', label: 'Sending Ops', icon: '◉' },
        { path: '/workspaces', label: 'Workspaces', icon: '⟳' },
        { path: '/contacts', label: 'Contacts', icon: '👥' },
        { path: '/templates', label: 'Templates', icon: '📋' },
        { path: '/list-cleaning', label: 'List Cleaning', icon: '🧹' },
        { path: '/account', label: 'Account', icon: '🏢' },
        { path: '/spam-checker', label: 'Spam Checker', icon: '🛡' },
        { path: '/inbox-test', label: 'Inbox Test', icon: '📬' },
        { path: '/settings', label: 'Settings', icon: '⚙' },
    ];

    const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
    const closeMobileNav = () => setMobileNavOpen(false);

    return (
        <div className="app-shell" style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
            <style>{`
                .stat-tip .cnt { display: none; }
                .stat-tip .pct { display: inline; }
                .stat-tip:hover .cnt { display: inline; }
                .stat-tip:hover .pct { display: none; }
            `}</style>
            <div className={`mobile-nav-backdrop${mobileNavOpen ? ' open' : ''}`} onClick={closeMobileNav} />
            <header className="mobile-header">
                <button className="mobile-menu-button" onClick={() => setMobileNavOpen(true)} aria-label="Open navigation">☰</button>
                <Link to="/" className="mobile-brand"><span>⟳</span> RELAY</Link>
                <Link to="/campaigns/new" className="mobile-new-campaign">New campaign</Link>
            </header>
            <div className={`app-sidebar${mobileNavOpen ? ' open' : ''}`} style={{ width: 220, background: C.card, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                <div style={{ padding: '20px 20px 16px', borderBottom: `1px solid ${C.border}` }}>
                    <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: C.text }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg, ${C.accent}, #a855f7)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>⟳</div>
                        <div>
                            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.02em' }}>RELAY</div>
                            <div style={{ fontSize: 8, color: C.muted, letterSpacing: '0.12em', fontWeight: 500 }}>EMAIL PLATFORM</div>
                        </div>
                    </Link>
                </div>

                {/* Account switcher */}
                {accounts.length > 1 && (
                    <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}` }}>
                        <select style={{ ...inputStyle, padding: '6px 10px', fontSize: 12 }}
                            value={currentAccount?.id || ''} onChange={e => api.switchAccount(e.target.value)}>
                            {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.role})</option>)}
                        </select>
                    </div>
                )}

                <div style={{ padding: '12px 10px', flex: 1 }}>
                    <Link to="/campaigns/new" onClick={closeMobileNav} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 8, background: C.accent, color: '#fff', fontSize: 13, fontWeight: 600, textDecoration: 'none', marginBottom: 12 }}>
                        ✏ New Campaign
                    </Link>
                    {navItems.map(item => (
                        <Link key={item.path} to={item.path} onClick={closeMobileNav} style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderRadius: 8,
                            background: isActive(item.path) ? C.active : 'transparent',
                            color: isActive(item.path) ? C.text : C.muted,
                            fontSize: 13, fontWeight: isActive(item.path) ? 600 : 400,
                            textDecoration: 'none', marginBottom: 2,
                        }}>
                            <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>{item.icon}</span>
                            {item.label}
                        </Link>
                    ))}
                </div>

                <div style={{ padding: 12, borderTop: `1px solid ${C.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, background: C.hover }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: C.accentDim, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: C.accent }}>
                            {(user?.name || user?.email || '?')[0].toUpperCase()}
                        </div>
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name || 'User'}</div>
                            <div style={{ fontSize: 10, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentAccount?.role || ''}</div>
                        </div>
                        <button onClick={logout} title="Logout" style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 14 }}>↪</button>
                    </div>
                </div>
            </div>
            <main className="app-main" style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>{children}</main>
        </div>
    );
}

// ═══════════════════════════════════════════════════════
//  APP ROUTER
// ═══════════════════════════════════════════════════════

function AppRoutes() {
    const { user } = useAuth();
    if (!user) return <LoginPage />;
    return (
        <Layout>
            <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/audience-analytics" element={<AudienceAnalyticsPage />} />
                <Route path="/campaigns" element={<CampaignsPage />} />
                <Route path="/operations" element={<OperationsPage />} />
                <Route path="/campaigns/new" element={<CampaignEditorPage />} />
                <Route path="/campaigns/edit/:id" element={<CampaignEditorPage />} />
                <Route path="/workspaces" element={<WorkspacesPage />} />
                <Route path="/contacts" element={<ContactsPage />} />
                <Route path="/templates" element={<TemplatesPage />} />
                <Route path="/list-cleaning" element={<ListCleaningPage />} />
                <Route path="/account" element={<AccountPage />} />
                <Route path="/spam-checker" element={<SpamCheckerPage />} />
                <Route path="/inbox-test" element={<InboxTestPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="*" element={<Navigate to="/" />} />
            </Routes>
        </Layout>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRoutes />
            </AuthProvider>
        </BrowserRouter>
    );
}
